import React, { useState, useEffect, useRef } from 'react';
import { Table, Input, Select, Checkbox, Modal } from 'antd';
import Cookies from "js-cookie";
import axios from "axios";
import configData from "../../config/config.json";
import { notifysuccess } from "../../utils/toast";
import { notifyfailure } from "../../utils/toast";
import backButton from '../../assets/images/back_button_arrow.svg';
import zoneIcon from '../../assets/images/icons/zone.svg';
import creativetemicon from '../../assets/newlayout/modal_toggle_icon.svg';
import { Link } from "react-router-dom";
import BlockCategoryTable from '../BlockCategoryTable';
import FullLoader from "../../assets/images/full-page-loader.gif";
import loaderIcon from "../../assets/images/login-and-signup/loader.gif";
import gamSizesData from '../../constants/GamSizes/sizes.json';
import gamVastMasterSizesData from '../../constants/GamSizes/vastMasterSizes.json';
import gamCompanionSizesData from '../../constants/GamSizes/companionSizes.json';
import searchIcon from '../../assets/images/icons/search.svg';

const { Option } = Select;

// ─── Constants ──────────────────────────────────────────────────────────────────

const ZONE_TYPE_DISPLAY = {
    "Prebid": "Prebid",
    "Open RTB": "Open RTB",
    "publica": "Vast Publica",
    "Vast Zone": "Vast Adkernel",
    "GAM": "GAM",
};

// ─── Shared field-level validators (same rules & messages as ZoneViewEdit) ───────

const SHARED_FIELD_VALIDATORS = {
    revShare: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        if (!/^\d*\.?\d*$/.test(s)) return "Value must be between 0.1 and 100";
        const n = Number(s);
        if (n < 0.1 || n > 100) return "Value must be between 0.1 and 100";
        return null;
    },
    minEcpm: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return null; // optional
        if (!/^\d*\.?\d*$/.test(s)) return "Value must be between 0.00001 and 50";
        const n = Number(s);
        if (n < 0.00001 || n > 50) return "Value must be between 0.00001 and 50";
        return null;
    },
    maxEcpm: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return null; // optional
        if (!/^\d*\.?\d*$/.test(s)) return "Value must be between 0.00001 and 50";
        const n = Number(s);
        if (n < 0.00001 || n > 50) return "Value must be between 0.00001 and 50";
        return null;
    },
    human: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "Value must be an integer between 0 and 100";
        if (!/^\d*$/.test(s)) return "Value must be an integer between 0 and 100";
        const n = Number(s);
        if (n < 0 || n > 100) return "Value must be an integer between 0 and 100";
        return null;
    },
    pixalate: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "Value must be an integer between 0 and 100";
        if (!/^\d*$/.test(s)) return "Value must be an integer between 0 and 100";
        const n = Number(s);
        if (n < 0 || n > 100) return "Value must be an integer between 0 and 100";
        return null;
    },
    doubleverify: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "Value must be an integer between 0 and 100";
        if (!/^\d*$/.test(s)) return "Value must be an integer between 0 and 100";
        const n = Number(s);
        if (n < 0 || n > 100) return "Value must be an integer between 0 and 100";
        return null;
    },
    targetEcpm: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        if (!/^\d*\.?\d*$/.test(s)) return "Value should be greater than 0 and less than 100";
        const n = Number(s);
        if (n <= 0 || n >= 100) return "Value should be greater than 0 and less than 100";
        return null;
    },
};

// ─── Per-zone-type validator maps ────────────────────────────────────────────────

const ZONE_NAME_VALIDATOR = (v) => {
    const s = String(v ?? "").trim();
    if (s === "") return "This field is required";
    return null;
};

const PREBID_FIELD_VALIDATORS = {
    zoneName: ZONE_NAME_VALIDATOR,
    revShare: SHARED_FIELD_VALIDATORS.revShare,
    minEcpm: SHARED_FIELD_VALIDATORS.minEcpm,
    maxEcpm: SHARED_FIELD_VALIDATORS.maxEcpm,
    human: SHARED_FIELD_VALIDATORS.human,
    // PIXALATE SUNSET: Pixalate validation disabled (column removed)
    // pixalate: SHARED_FIELD_VALIDATORS.pixalate,
    doubleverify: SHARED_FIELD_VALIDATORS.doubleverify,
};

const OPENRTB_FIELD_VALIDATORS = {
    zoneName: ZONE_NAME_VALIDATOR,
    revShare: SHARED_FIELD_VALIDATORS.revShare,
    minEcpm: SHARED_FIELD_VALIDATORS.minEcpm,
    maxEcpm: SHARED_FIELD_VALIDATORS.maxEcpm,
    human: SHARED_FIELD_VALIDATORS.human,
    // PIXALATE SUNSET: Pixalate validation disabled (column removed)
    // pixalate: SHARED_FIELD_VALIDATORS.pixalate,
    doubleverify: SHARED_FIELD_VALIDATORS.doubleverify,
};

// ─── Prebid: editable fields & Apply-All initial state ──────────────────────────

const PREBID_EDITABLE_FIELDS = [
    "zoneName", "status", "revShare", "minEcpm", "maxEcpm", "isHighestBidFloor",
    "human", /* PIXALATE SUNSET: "pixalate", */ "doubleverify", "passIPD", "partnerDomain", "blockCategories",
];

const INITIAL_APPLY_ALL_PREBID = {
    zoneName: "",
    status: null,
    revShare: "",
    minEcpm: "",
    maxEcpm: "",
    isHighestBidFloor: null,
    human: "",
    // PIXALATE SUNSET: column removed
    // pixalate: "",
    doubleverify: "",
    passIPD: false,
    partnerDomain: "",
    blockCategories: new Set(),
};

// ─── Open RTB: editable fields & Apply-All initial state ────────────────────────

const OPENRTB_EDITABLE_FIELDS = [
    "zoneName", "status", "revShare", "minEcpm", "maxEcpm",
    "human", /* PIXALATE SUNSET: "pixalate", */ "doubleverify", "passIPD", "partnerDomain", "blockCategories",
];

const INITIAL_APPLY_ALL_OPENRTB = {
    zoneName: "",
    status: null,
    revShare: "",
    minEcpm: "",
    maxEcpm: "",
    human: "",
    // PIXALATE SUNSET: column removed
    // pixalate: "",
    doubleverify: "",
    passIPD: false,
    partnerDomain: "",
    blockCategories: new Set(),
};

// ─── Vast Zone (ADK): validators, editable fields & Apply-All initial state ─────

const VAST_FIELD_VALIDATORS = {
    zoneName: ZONE_NAME_VALIDATOR,
    human: SHARED_FIELD_VALIDATORS.human,
    // PIXALATE SUNSET: Pixalate validation disabled (column removed)
    // pixalate: SHARED_FIELD_VALIDATORS.pixalate,
    doubleverify: SHARED_FIELD_VALIDATORS.doubleverify,
};

const VAST_EDITABLE_FIELDS = [
    "zoneName", "status", "isTargetEcpm", "targetEcpm",
    "human", /* PIXALATE SUNSET: "pixalate", */ "doubleverify", "passIPD", "partnerDomain", "blockCategories", "template",
];

const INITIAL_APPLY_ALL_VAST = {
    zoneName: "",
    status: null,
    isTargetEcpm: false,
    targetEcpm: "",
    human: "",
    // PIXALATE SUNSET: column removed
    // pixalate: "",
    doubleverify: "",
    passIPD: false,
    partnerDomain: "",
    blockCategories: new Set(),
    template: null,
};

// ─── Publica: validators, editable fields & Apply-All initial state ─────────

const PUBLICA_FIELD_VALIDATORS = {
    channelName: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        return null;
    },
    region: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        return null;
    },
    bidAdjustment: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        if (!/^\d*\.?\d{0,2}$/.test(s)) return "Value must be between 0 - 100";
        const n = Number(s);
        if (n < 0 || n > 100) return "Value must be between 0 - 100";
        return null;
    },
    bidAdjustmentValue: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return null; // optional
        if (!/^\d*\.?\d{0,2}$/.test(s)) return "Value should be greater than or equal to 0";
        const n = Number(s);
        if (n < 0) return "Value should be greater than or equal to 0";
        return null;
    },
    asi: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        return null;
    },
    sid: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        return null;
    },
    scName: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        return null;
    },
    scDomain: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        return null;
    },
};

const PUBLICA_EDITABLE_FIELDS = [
    "status", "hbVastVersion", "channelName", "region",
    "isTargetEcpm", "targetEcpm", "bidAdjustment", "bidAdjustmentValue",
    "asi", "sid", "scName", "scDomain",
    "passIPD", "partnerDomain", "template",
];

const INITIAL_APPLY_ALL_PUBLICA = {
    status: null,
    hbVastVersion: null,
    channelName: "",
    region: null,
    isTargetEcpm: false,
    targetEcpm: "",
    bidAdjustment: "",
    bidAdjustmentValue: "",
    asi: "",
    sid: "",
    scName: "",
    scDomain: "",
    passIPD: false,
    partnerDomain: "",
    template: null,
};

// const PUBLICA_TRAFFIC_TYPE_OPTIONS = [
//     { label: "Website", value: "Website" },
//     { label: "Inapp", value: "Inapp" },
//     { label: "CTV", value: "CTV" },
// ];

const PUBLICA_VAST_VERSION_OPTIONS = [
    { label: "2", value: "2" },
    { label: "3", value: "3" },
    { label: "4", value: "4" },
    { label: "4.1", value: "4.1" },
];

const PUBLICA_REGION_OPTIONS = [
    { label: "APAC", value: "ap-southeast-2" },
    { label: "Europe", value: "eu-west-1" },
    { label: "Japan", value: "ap-northeast-1" },
    { label: "USA", value: "us-east-1" },
];

// ─── GAM: validators, editable fields & Apply-All initial state ─────────────────
// (Bulk edit is only available to sa/mobops → GAM is never role-restricted here,
//  so TV app / Override-UPR / UPR-Category fields are always shown & editable.)

const GAM_FIELD_VALIDATORS = {
    zoneName: (v) => {
        const s = String(v ?? "").trim();
        if (s === "") return "This field is required";
        if (s.length > 90) return "Maximum 90 characters allowed";
        return null;
    },
    sizes: (v) => {
        if (!Array.isArray(v) || v.length === 0) return "Minimum 1 selection is required";
        return null;
    },
};

const GAM_EDITABLE_FIELDS = [
    "zoneName", "status", "sizes", "companion", "tvApplication",
    "overrideUpr", "overrideSetUpr", "uprCategory",
];

const INITIAL_APPLY_ALL_GAM = {
    zoneName: "",
    status: null,
    sizes: [],
    companion: [],
    tvAppCheckbox: false,
    tvApplication: "",
    overrideUpr: false,
    overrideSetUpr: "",
    uprCategory: null,
};

// ─── Sample API JSON: Prebid (fallback when zone/zoneDetails is unavailable) ────
// Mirrors a zone/zoneDetails response for a Prebid zone.
// NOTE: Sample data and generators commented out – currently unused (fallback via API only)

// const SAMPLE_PREBID_DATA = [
//     { z_Id: "ZN_PRE_101", z_name: "Homepage Top Banner", p_adkId: "PUB_201", invent_type: "Website", traffic_type: "Banner,Video", bannerCheckbox: true, visible: true, exchange_type: "OPEN_RTB", isHighestBidFloor: true, isTargetEcpm: false, target_ecpm: 0, status: "enabled", revshare: "65", minEcpm: 1.5, maxEcpm: 12, req_daily: 120000, imp_daily: 85000, sampling: { human: 70, pixalate: 40 }, ipd_bool: true, ipd_value: "partner-domain-1.com", block_category: ["IAB25-3", "IAB26-1"] },
//     { z_Id: "ZN_PRE_102", z_name: "Sidebar Ad Unit", p_adkId: "PUB_202", invent_type: "Website", traffic_type: "Banner", bannerCheckbox: false, visible: true, exchange_type: "HEADER_BIDDING", isHighestBidFloor: false, isTargetEcpm: false, target_ecpm: 0, status: "enabled", revshare: "72", minEcpm: 0.8, maxEcpm: 8, req_daily: 95000, imp_daily: 62000, sampling: { human: 55, pixalate: 35 }, ipd_bool: false, ipd_value: "", block_category: ["IAB25-1"] },
//     { z_Id: "ZN_PRE_103", z_name: "Footer Video Slot", p_adkId: "PUB_203", invent_type: "App", traffic_type: "Video", bannerCheckbox: null, visible: false, exchange_type: "OPEN_RTB", isHighestBidFloor: true, isTargetEcpm: true, target_ecpm: 5.0, status: "disabled", revshare: "58", minEcpm: 3.0, maxEcpm: 20, req_daily: 45000, imp_daily: 18000, sampling: { human: 80, pixalate: 25 }, ipd_bool: true, ipd_value: "video-partner.io", block_category: [] },
//     { z_Id: "ZN_PRE_104", z_name: "In-Article Native", p_adkId: "PUB_204", invent_type: "Website", traffic_type: "Banner,Native", bannerCheckbox: true, visible: true, exchange_type: "OPEN_RTB", isHighestBidFloor: false, isTargetEcpm: false, target_ecpm: 0, status: "enabled", revshare: "68", minEcpm: 2.0, maxEcpm: 15, req_daily: 200000, imp_daily: 140000, sampling: { human: 65, pixalate: 50 }, ipd_bool: false, ipd_value: "", block_category: ["IAB25-2", "IAB26-4", "IAB25-5"] },
//     { z_Id: "ZN_PRE_105", z_name: "Interstitial Mobile", p_adkId: "PUB_205", invent_type: "App", traffic_type: "Banner,Video", bannerCheckbox: false, visible: true, exchange_type: "HEADER_BIDDING", isHighestBidFloor: true, isTargetEcpm: false, target_ecpm: 0, status: "enabled", revshare: "75", minEcpm: 5.0, maxEcpm: 30, req_daily: 310000, imp_daily: 175000, sampling: { human: 90, pixalate: 20 }, ipd_bool: true, ipd_value: "mobile-exchange.net", block_category: ["IAB25-3"] },
// ];

// const generateSamplePrebidZoneDetail = (zone, idx) => {
//     const sample = SAMPLE_PREBID_DATA[idx % SAMPLE_PREBID_DATA.length];
//     return {
//         ...sample,
//         z_Id: zone.z_Id || zone.id || sample.z_Id,
//         z_name: zone.z_name || sample.z_name,
//         p_adkId: zone.p_adkId || sample.p_adkId,
//         invent_type: zone.invent_type || sample.invent_type,
//         traffic_type: zone.traffic_type || sample.traffic_type,
//         status: zone.status || sample.status,
//     };
// };

// ─── Sample API JSON: Open RTB (fallback when zone/zoneDetails is unavailable) ──

// const SAMPLE_OPENRTB_DATA = [
//     { z_Id: "ZN_ORT_201", z_name: "Video Pre-Roll", p_adkId: "PUB_301", invent_type: "Website", traffic_type: "Video", bannerCheckbox: null, visible: true, exchange_type: "OPEN_RTB", isHighestBidFloor: false, isTargetEcpm: false, target_ecpm: 0, status: "enabled", revshare: "60", minEcpm: 4.0, maxEcpm: 18, req_daily: 88000, imp_daily: 52000, sampling: { human: 75, pixalate: 30 }, ipd_bool: true, ipd_value: "rtb-partner.com", block_category: ["IAB25-1", "IAB26-2"] },
//     { z_Id: "ZN_ORT_202", z_name: "Display Leaderboard", p_adkId: "PUB_302", invent_type: "Website", traffic_type: "Banner,Video", bannerCheckbox: true, visible: true, exchange_type: "OPEN_RTB", isHighestBidFloor: true, isTargetEcpm: false, target_ecpm: 0, status: "enabled", revshare: "68", minEcpm: 2.0, maxEcpm: 14, req_daily: 150000, imp_daily: 95000, sampling: { human: 60, pixalate: 45 }, ipd_bool: false, ipd_value: "", block_category: ["IAB25-5"] },
//     { z_Id: "ZN_ORT_203", z_name: "App Rewarded Video", p_adkId: "PUB_303", invent_type: "App", traffic_type: "Video", bannerCheckbox: null, visible: false, exchange_type: "OPEN_RTB", isHighestBidFloor: false, isTargetEcpm: true, target_ecpm: 8.0, status: "disabled", revshare: "55", minEcpm: 6.0, maxEcpm: 25, req_daily: 60000, imp_daily: 28000, sampling: { human: 85, pixalate: 15 }, ipd_bool: true, ipd_value: "rewarded-vid.net", block_category: [] },
//     { z_Id: "ZN_ORT_204", z_name: "Mid-Roll Outstream", p_adkId: "PUB_304", invent_type: "Website", traffic_type: "Banner,Native", bannerCheckbox: false, visible: true, exchange_type: "HEADER_BIDDING", isHighestBidFloor: true, isTargetEcpm: false, target_ecpm: 0, status: "enabled", revshare: "72", minEcpm: 1.0, maxEcpm: 10, req_daily: 220000, imp_daily: 160000, sampling: { human: 50, pixalate: 55 }, ipd_bool: false, ipd_value: "", block_category: ["IAB26-1", "IAB25-3", "IAB26-4"] },
//     { z_Id: "ZN_ORT_205", z_name: "Sticky Footer Banner", p_adkId: "PUB_305", invent_type: "App", traffic_type: "Banner", bannerCheckbox: true, visible: true, exchange_type: "OPEN_RTB", isHighestBidFloor: false, isTargetEcpm: false, target_ecpm: 0, status: "enabled", revshare: "78", minEcpm: 3.5, maxEcpm: 22, req_daily: 180000, imp_daily: 110000, sampling: { human: 95, pixalate: 10 }, ipd_bool: true, ipd_value: "sticky-exchange.io", block_category: ["IAB25-2"] },
// ];

// const generateSampleOpenRTBZoneDetail = (zone, idx) => {
//     const sample = SAMPLE_OPENRTB_DATA[idx % SAMPLE_OPENRTB_DATA.length];
//     return {
//         ...sample,
//         z_Id: zone.z_Id || zone.id || sample.z_Id,
//         z_name: zone.z_name || sample.z_name,
//         p_adkId: zone.p_adkId || sample.p_adkId,
//         invent_type: zone.invent_type || sample.invent_type,
//         traffic_type: zone.traffic_type || sample.traffic_type,
//         status: zone.status || sample.status,
//     };
// };

// ─── Sample API JSON: Vast Zone ADK (fallback when zone/zoneDetails is unavailable) ─

// const SAMPLE_VAST_DATA = [
//     { z_Id: "ZN_VST_301", z_name: "Pre-Roll Video AD", p_adkId: "PUB_401", invent_type: "Website", traffic_type: "Video", visible: true, stream_position: "Instream", fixed_cpm: 8.5, isTargetEcpm: false, target_ecpm: 0, status: "enabled", req_daily: 95000, imp_daily: 48000, sampling: { human: 70, pixalate: 35 }, ipd_bool: true, ipd_value: "vast-partner.com", block_category: ["IAB25-3", "IAB26-1"], vastTagInfo: [{ label: "Default VAST Template", value: "tpl_default" }] },
//     { z_Id: "ZN_VST_302", z_name: "Mid-Roll Outstream", p_adkId: "PUB_402", invent_type: "Website", traffic_type: "Video,Banner", visible: true, stream_position: "Outstream", fixed_cpm: 5.2, isTargetEcpm: true, target_ecpm: 6.5, status: "enabled", req_daily: 120000, imp_daily: 72000, sampling: { human: 60, pixalate: 40 }, ipd_bool: false, ipd_value: "", block_category: ["IAB25-1"], vastTagInfo: [] },
//     { z_Id: "ZN_VST_303", z_name: "CTV Pre-Roll", p_adkId: "PUB_403", invent_type: "CTV", traffic_type: "Video", visible: true, stream_position: "Instream", fixed_cpm: 15.0, isTargetEcpm: false, target_ecpm: 0, status: "enabled", req_daily: 40000, imp_daily: 22000, sampling: { human: 90, pixalate: 20 }, ipd_bool: true, ipd_value: "ctv-exchange.io", block_category: [], vastTagInfo: [{ label: "CTV Template", value: "tpl_ctv" }] },
//     { z_Id: "ZN_VST_304", z_name: "Inapp Rewarded", p_adkId: "PUB_404", invent_type: "Inapp", traffic_type: "Video", visible: false, stream_position: "Instream", fixed_cpm: 10.0, isTargetEcpm: true, target_ecpm: 12.0, status: "disabled", req_daily: 55000, imp_daily: 30000, sampling: { human: 75, pixalate: 25 }, ipd_bool: false, ipd_value: "", block_category: ["IAB25-2", "IAB26-4"], vastTagInfo: [] },
//     { z_Id: "ZN_VST_305", z_name: "Post-Roll Banner", p_adkId: "PUB_405", invent_type: "Website", traffic_type: "Banner,Video", visible: true, stream_position: "Outstream", fixed_cpm: 3.8, isTargetEcpm: false, target_ecpm: 0, status: "enabled", req_daily: 200000, imp_daily: 130000, sampling: { human: 55, pixalate: 50 }, ipd_bool: true, ipd_value: "banner-vast.net", block_category: ["IAB25-5"], vastTagInfo: [{ label: "Standard Vast Tag", value: "tpl_standard" }] },
// ];

// const generateSampleVastZoneDetail = (zone, idx) => {
//     const sample = SAMPLE_VAST_DATA[idx % SAMPLE_VAST_DATA.length];
//     return {
//         ...sample,
//         z_Id: zone.z_Id || zone.id || sample.z_Id,
//         z_name: zone.z_name || sample.z_name,
//         p_adkId: zone.p_adkId || sample.p_adkId,
//         invent_type: zone.invent_type || sample.invent_type,
//         traffic_type: zone.traffic_type || sample.traffic_type,
//         status: zone.status || sample.status,
//     };
// };

// ─── Sample API JSON: Publica (fallback) ─────────────────────────────────────

// const SAMPLE_PUBLICA_DATA = [
//     { z_Id: "ZN_PUB_401", z_name: "CTV Pre-Roll Publica", p_adkId: "PUB_501", invent_type: "CTV", traffic_type: "Video", visible: true, eCpm: 12.5, isTargetEcpm: false, target_ecpm: 0, header_bidder_vast_version: "3", channel_name: "sports_channel", region: "us-east-1", bid_adjustment: "85", bid_adjustment_value: "2.50", asi: "example.com", sid: "pub-12345", name: "Example Publisher", domain: "example.com", status: "enabled", req_daily: 80000, imp_daily: 45000, sampling: { human: 70, pixalate: 30 }, ipd_bool: true, ipd_value: "publica-partner.com", template: { label: "CTV Template", value: "tpl_ctv_01" } },
//     { z_Id: "ZN_PUB_402", z_name: "Inapp Mid-Roll", p_adkId: "PUB_502", invent_type: "Inapp", traffic_type: "Video,Banner", visible: true, eCpm: 8.0, isTargetEcpm: true, target_ecpm: 9.5, header_bidder_vast_version: "4", channel_name: "gaming_app", region: "eu-west-1", bid_adjustment: "72", bid_adjustment_value: "1.80", asi: "gaming.com", sid: "pub-67890", name: "Gaming Network", domain: "gaming.com", status: "enabled", req_daily: 120000, imp_daily: 68000, sampling: { human: 60, pixalate: 40 }, ipd_bool: false, ipd_value: "", template: { label: "Inapp VAST", value: "tpl_inapp_01" } },
//     { z_Id: "ZN_PUB_403", z_name: "Website Outstream", p_adkId: "PUB_503", invent_type: "Website", traffic_type: "Video", visible: true, eCpm: 6.0, isTargetEcpm: false, target_ecpm: 0, header_bidder_vast_version: "3", channel_name: "news_site", region: "ap-southeast-2", bid_adjustment: "90", bid_adjustment_value: "3.00", asi: "newsportal.com", sid: "pub-11111", name: "News Portal", domain: "newsportal.com", status: "enabled", req_daily: 200000, imp_daily: 130000, sampling: { human: 80, pixalate: 20 }, ipd_bool: true, ipd_value: "news-exchange.io", template: { label: "Web Template", value: "tpl_web_01" } },
//     { z_Id: "ZN_PUB_404", z_name: "CTV Post-Roll", p_adkId: "PUB_504", invent_type: "CTV", traffic_type: "Video", visible: false, eCpm: 18.0, isTargetEcpm: true, target_ecpm: 20.0, header_bidder_vast_version: "4.1", channel_name: "movie_channel", region: "ap-northeast-1", bid_adjustment: "65", bid_adjustment_value: "4.50", asi: "movies.tv", sid: "pub-22222", name: "Movie Channel", domain: "movies.tv", status: "disabled", req_daily: 35000, imp_daily: 15000, sampling: { human: 90, pixalate: 15 }, ipd_bool: false, ipd_value: "", template: { label: "CTV Premium", value: "tpl_ctv_02" } },
//     { z_Id: "ZN_PUB_405", z_name: "Inapp Rewarded Video", p_adkId: "PUB_505", invent_type: "Inapp", traffic_type: "Video", visible: true, eCpm: 10.0, isTargetEcpm: false, target_ecpm: 0, header_bidder_vast_version: "2", channel_name: "fitness_app", region: "us-east-1", bid_adjustment: "78", bid_adjustment_value: "2.00", asi: "fitnessapp.com", sid: "pub-33333", name: "Fitness App", domain: "fitnessapp.com", status: "enabled", req_daily: 95000, imp_daily: 52000, sampling: { human: 55, pixalate: 45 }, ipd_bool: true, ipd_value: "fitness-ads.net", template: { label: "Inapp Rewarded", value: "tpl_inapp_02" } },
// ];

// const generateSamplePublicaZoneDetail = (zone, idx) => {
//     const sample = SAMPLE_PUBLICA_DATA[idx % SAMPLE_PUBLICA_DATA.length];
//     return {
//         ...sample,
//         z_Id: zone.z_Id || zone.id || sample.z_Id,
//         z_name: zone.z_name || sample.z_name,
//         p_adkId: zone.p_adkId || sample.p_adkId,
//         invent_type: zone.invent_type || sample.invent_type,
//         traffic_type: zone.traffic_type || sample.traffic_type,
//         status: zone.status || sample.status,
//     };
// };

// ─── Build a row object from a zone detail API response ─────────────────────────

// ─── Zone-type-specific row builders ────────────────────────────────────────────

function buildPrebidRow(detail, idx) {
    const rawFmt = detail.traffic_type;
    const formatTypeArr = Array.isArray(rawFmt)
        ? rawFmt
        : (rawFmt ? rawFmt.split(",").map((s) => s.trim()) : []);
    const statusStr = detail.status || "enabled";
    const isActiveVal = detail.visible !== undefined ? !!detail.visible : statusStr === "enabled";
    const samplingHuman = detail.sampling?.human ?? detail.sampling_human;
    const samplingPixalate = detail.sampling?.pixalate ?? detail.sampling_pixalate;
    const samplingDv = detail.sampling?.dv ?? detail.sampling_dv;

    return {
        key: String(detail.z_Id || idx),
        zoneId: detail.z_Id || String(idx),
        zoneName: detail.z_name || `Zone ${idx + 1}`,
        publisherId: detail.p_adkId || detail.publisher_id || "",
        trafficType: detail.invent_type || "",
        formatType: formatTypeArr,
        bannerCheckbox: detail.bannerCheckbox ?? null,
        isActive: isActiveVal,
        exchangeType: detail.exchange_type || "",
        isHighestBidFloor: !!detail.isHighestBidFloor,
        isTargetEcpm: !!detail.isTargetEcpm,
        targetEcpm: detail.target_ecpm !== undefined && detail.target_ecpm !== null
            ? String(detail.target_ecpm) : "",
        fixedCpm: detail.fixed_cpm !== undefined && detail.fixed_cpm !== null
            ? String(detail.fixed_cpm) : "",
        reqDaily: detail.req_daily !== undefined && detail.req_daily !== null
            ? detail.req_daily : "",
        impDaily: detail.imp_daily !== undefined && detail.imp_daily !== null
            ? detail.imp_daily : "",
        status: statusStr,
        revShare: detail.revshare !== undefined && detail.revshare !== null
            ? String(detail.revshare) : "",
        minEcpm: detail.minEcpm !== undefined && detail.minEcpm !== null
            ? String(detail.minEcpm) : "",
        maxEcpm: detail.maxEcpm !== undefined && detail.maxEcpm !== null
            ? String(detail.maxEcpm) : "",
        human: samplingHuman !== undefined && samplingHuman !== null
            ? String(samplingHuman) : "",
        // PIXALATE SUNSET: Pixalate no longer returned by API
        // pixalate: samplingPixalate !== undefined && samplingPixalate !== null
        //     ? String(samplingPixalate) : "",
        doubleverify: samplingDv !== undefined && samplingDv !== null
            ? String(samplingDv) : "",
        passIPD: !!detail.ipd_bool,
        partnerDomain: detail.ipd_value || "",
        blockCategories: new Set(detail.block_category || []),
        template: detail.template?.value || "",
        manualOverrideFields: new Set(),
    };
}

function buildOpenRTBRow(detail, idx) {
    const rawFmt = detail.traffic_type;
    const formatTypeArr = Array.isArray(rawFmt)
        ? rawFmt
        : (rawFmt ? rawFmt.split(",").map((s) => s.trim()) : []);
    const statusStr = detail.status || "enabled";
    const isActiveVal = detail.visible !== undefined ? !!detail.visible : statusStr === "enabled";
    const samplingHuman = detail.sampling?.human ?? detail.sampling_human;
    const samplingPixalate = detail.sampling?.pixalate ?? detail.sampling_pixalate;
    const samplingDv = detail.sampling?.dv ?? detail.sampling_dv;

    return {
        key: String(detail.z_Id || idx),
        zoneId: detail.z_Id || String(idx),
        zoneName: detail.z_name || `Zone ${idx + 1}`,
        publisherId: detail.p_adkId || detail.publisher_id || "",
        trafficType: detail.invent_type || "",
        formatType: formatTypeArr,
        bannerCheckbox: detail.bannerCheckbox ?? null,
        isActive: isActiveVal,
        exchangeType: detail.exchange_type || "",
        isHighestBidFloor: !!detail.isHighestBidFloor,
        isTargetEcpm: !!detail.isTargetEcpm,
        targetEcpm: detail.target_ecpm !== undefined && detail.target_ecpm !== null
            ? String(detail.target_ecpm) : "",
        fixedCpm: detail.fixed_cpm !== undefined && detail.fixed_cpm !== null
            ? String(detail.fixed_cpm) : "",
        streamPosition: detail.stream_position || "",
        reqDaily: detail.req_daily !== undefined && detail.req_daily !== null
            ? detail.req_daily : "",
        impDaily: detail.imp_daily !== undefined && detail.imp_daily !== null
            ? detail.imp_daily : "",
        status: statusStr,
        revShare: detail.revshare !== undefined && detail.revshare !== null
            ? String(detail.revshare) : "",
        minEcpm: detail.minEcpm !== undefined && detail.minEcpm !== null
            ? String(detail.minEcpm) : "",
        maxEcpm: detail.maxEcpm !== undefined && detail.maxEcpm !== null
            ? String(detail.maxEcpm) : "",
        human: samplingHuman !== undefined && samplingHuman !== null
            ? String(samplingHuman) : "",
        // PIXALATE SUNSET: Pixalate no longer returned by API
        // pixalate: samplingPixalate !== undefined && samplingPixalate !== null
        //     ? String(samplingPixalate) : "",
        doubleverify: samplingDv !== undefined && samplingDv !== null
            ? String(samplingDv) : "",
        passIPD: !!detail.ipd_bool,
        partnerDomain: detail.ipd_value || "",
        blockCategories: new Set(detail.block_category || []),
        manualOverrideFields: new Set(),
    };
}

function buildVastZoneRow(detail, idx) {
    const rawFmt = detail.traffic_type;
    const formatTypeArr = Array.isArray(rawFmt)
        ? rawFmt
        : (rawFmt ? rawFmt.split(",").map((s) => s.trim()) : []);
    const statusStr = detail.status || "enabled";
    const isActiveVal = detail.visible !== undefined ? !!detail.visible : statusStr === "enabled";
    const samplingHuman = detail.sampling?.human ?? detail.sampling_human;
    const samplingPixalate = detail.sampling?.pixalate ?? detail.sampling_pixalate;
    const samplingDv = detail.sampling?.dv ?? detail.sampling_dv;

    return {
        key: String(detail.z_Id || idx),
        zoneId: detail.z_Id || String(idx),
        zoneName: detail.z_name || `Zone ${idx + 1}`,
        publisherId: detail.p_adkId || detail.publisher_id || "",
        trafficType: detail.invent_type || "",
        formatType: formatTypeArr,
        isActive: isActiveVal,
        isTargetEcpm: !!detail.isTargetEcpm,
        targetEcpm: detail.target_ecpm !== undefined && detail.target_ecpm !== null
            ? String(detail.target_ecpm) : "",
        fixedCpm: detail.fixed_cpm !== undefined && detail.fixed_cpm !== null
            ? String(detail.fixed_cpm) : "",
        streamPosition: detail.stream_position || "",
        template: detail.vastTagInfo?.[0]?.label || "",
        reqDaily: detail.req_daily !== undefined && detail.req_daily !== null
            ? detail.req_daily : "",
        impDaily: detail.imp_daily !== undefined && detail.imp_daily !== null
            ? detail.imp_daily : "",
        status: statusStr,
        revShare: detail.revshare !== undefined && detail.revshare !== null
            ? String(detail.revshare) : "",
        minEcpm: detail.minEcpm !== undefined && detail.minEcpm !== null
            ? String(detail.minEcpm) : "",
        maxEcpm: detail.maxEcpm !== undefined && detail.maxEcpm !== null
            ? String(detail.maxEcpm) : "",
        human: samplingHuman !== undefined && samplingHuman !== null
            ? String(samplingHuman) : "",
        // PIXALATE SUNSET: Pixalate no longer returned by API
        // pixalate: samplingPixalate !== undefined && samplingPixalate !== null
        //     ? String(samplingPixalate) : "",
        doubleverify: samplingDv !== undefined && samplingDv !== null
            ? String(samplingDv) : "",
        passIPD: !!detail.ipd_bool,
        partnerDomain: detail.ipd_value || "",
        blockCategories: new Set(detail.block_category || []),
        manualOverrideFields: new Set(),
    };
}

function buildPublicaRow(detail, idx) {
    const rawFmt = detail.traffic_type;
    const formatTypeArr = Array.isArray(rawFmt)
        ? rawFmt
        : (rawFmt ? rawFmt.split(",").map((s) => s.trim()) : []);
    const statusStr = detail.status || "enabled";
    const isActiveVal = detail.visible !== undefined ? !!detail.visible : statusStr === "enabled";
    const samplingHuman = detail.sampling?.human ?? detail.sampling_human;
    const samplingPixalate = detail.sampling?.pixalate ?? detail.sampling_pixalate;
    const samplingDv = detail.sampling?.dv ?? detail.sampling_dv;

    return {
        key: String(detail.z_Id || idx),
        zoneId: detail.z_Id || String(idx),
        channelName: detail.z_name || detail.channel_name || "",
        publisherId: detail.p_adkId || detail.publisher_id || "",
        trafficType: detail.invent_type || "",
        formatType: formatTypeArr,
        isActive: isActiveVal,
        hbVastVersion: detail.header_bidder_vast_version || "3",
        region: detail.region || "",
        eCpm: detail.eCpm !== undefined && detail.eCpm !== null ? String(detail.eCpm) : "",
        isTargetEcpm: !!detail.isTargetEcpm,
        targetEcpm: detail.target_ecpm !== undefined && detail.target_ecpm !== null
            ? String(detail.target_ecpm) : "",
        bidAdjustment: detail.bid_adjustment !== undefined && detail.bid_adjustment !== null
            ? String(detail.bid_adjustment) : "",
        bidAdjustmentValue: detail.bid_adjustment_value !== undefined && detail.bid_adjustment_value !== null
            ? String(detail.bid_adjustment_value) : "",
        asi: detail.asi || "",
        sid: detail.sid || "",
        scName: detail.SchainName || detail.name || "",
        scDomain: detail.domain || "",
        passIPD: !!detail.isAddValueCheckbox,
        partnerDomain: detail.override || "",
        template: detail.publicaData?.label || detail.template?.value || "",
        human: samplingHuman !== undefined && samplingHuman !== null
            ? String(samplingHuman) : "",
        pixalate: samplingPixalate !== undefined && samplingPixalate !== null
            ? String(samplingPixalate) : "",
        doubleverify: samplingDv !== undefined && samplingDv !== null
            ? String(samplingDv) : "",
        status: statusStr,
        manualOverrideFields: new Set(),
    };
}

function buildGamRow(detail, idx) {
    const platform = detail.invent_type || "Website";       // Website | Inapp | CTV
    const format = detail.traffic_type || "Video";          // Video | Banner
    const isVideo = format === "Video";
    const sizesObj = detail.gamData?.sizes || {};
    const sizes = isVideo
        ? (Array.isArray(sizesObj.vastSizes) ? sizesObj.vastSizes : [])
        : (Array.isArray(sizesObj.bannerSizes) ? sizesObj.bannerSizes : []);
    const companion = isVideo && Array.isArray(sizesObj.companionSizes) ? sizesObj.companionSizes : [];
    const applicationId = detail.gamData?.adUnit?.applicationId || "";
    const upr = detail.gamData?.upr || {};
    // GAM uses a 3-state status like the other zone types. The bulk detail may
    // return it as a string ("enabled"/"disabled"/"archived") or a legacy boolean.
    const statusStr = typeof detail.status === "string"
        ? detail.status
        : (detail.status === false ? "disabled" : "enabled");

    return {
        key: String(detail.z_Id || idx),
        zoneId: detail.z_Id || String(idx),
        zoneName: detail.z_name || `Zone ${idx + 1}`,
        publisherId: detail.p_adkId || detail.publisher_id || "",
        trafficType: platform,
        formatType: format,
        status: statusStr,
        isActive: statusStr === "enabled",
        sizes,
        companion,
        tvAppCheckbox: platform === "CTV" && applicationId !== "",
        tvApplication: applicationId,
        // Display-only (non-editable) pricing values
        fixedPrice: detail.fixed_cpm !== undefined && detail.fixed_cpm !== null ? String(detail.fixed_cpm) : "",
        upr: upr.set_upr !== undefined && upr.set_upr !== null ? String(upr.set_upr) : "",
        // Editable pricing
        overrideUpr: !!detail.isTargetEcpm,
        overrideSetUpr: detail.target_ecpm !== undefined && detail.target_ecpm !== null ? String(detail.target_ecpm) : "",
        uprCategory: upr.upr_id || "",
        uprCategoryNotFound: false,
        uprCategoryMatched: false,
        manualOverrideFields: new Set(),
    };
}

// ─── SVG helpers ────────────────────────────────────────────────────────────────

const ChevronDown = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 6L8 10L12 6" stroke="#94A3B8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <g clipPath="url(#bzei_clip)">
            <path d="M8.00016 14.6667C11.6821 14.6667 14.6668 11.6819 14.6668 8C14.6668 4.3181 11.6821 1.33334 8.00016 1.33334C4.31826 1.33334 1.3335 4.3181 1.3335 8C1.3335 11.6819 4.31826 14.6667 8.00016 14.6667Z" stroke="#155DFC" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 5.33334V8" stroke="#155DFC" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 10.6667H8.00667" stroke="#155DFC" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <defs><clipPath id="bzei_clip"><rect width="16" height="16" fill="white" /></clipPath></defs>
    </svg>
);

const BlockCatIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <g clipPath="url(#bzei_bcat)">
            <path d="M10.6665 3.33334H5.33317C2.75584 3.33334 0.666504 5.42268 0.666504 8.00001C0.666504 10.5773 2.75584 12.6667 5.33317 12.6667H10.6665C13.2438 12.6667 15.3332 10.5773 15.3332 8.00001C15.3332 5.42268 13.2438 3.33334 10.6665 3.33334Z" stroke="#B795EC" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10.6665 10C11.7711 10 12.6665 9.10457 12.6665 8C12.6665 6.89543 11.7711 6 10.6665 6C9.56193 6 8.6665 6.89543 8.6665 8C8.6665 9.10457 9.56193 10 10.6665 10Z" stroke="#B795EC" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <defs><clipPath id="bzei_bcat"><rect width="16" height="16" fill="white" /></clipPath></defs>
    </svg>
);

const YELLOW_DOT_STYLE = {
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#F59E0B",
    flexShrink: 0,
    marginRight: "6px",
    marginTop: "2px",
};

// ─── Component ──────────────────────────────────────────────────────────────────

const BulkZoneEdit = ({ handlePageViewChange, selectedZones = [], zoneType = "" }) => {

    const [formVisible, setFormVisible] = useState(false);

    useEffect(() => {
        const inputs = document.querySelectorAll('.ant-select-selection-search-input');

        inputs.forEach(input => {
            const handleInput = () => {
                const parent = input.closest('.ant-select-selection-search');
                if (input.value.length > 0) {
                    parent.classList.add('searching');
                } else {
                    parent.classList.remove('searching');
                }
            };

            input.addEventListener('input', handleInput);

            // Cleanup event listener on component unmount
            return () => {
                input.removeEventListener('input', handleInput);
            };
        });
    });

    const getInitialApplyAll = () => {
        if (zoneType === "Open RTB") return { ...INITIAL_APPLY_ALL_OPENRTB, blockCategories: new Set() };
        if (zoneType === "Vast Zone") return { ...INITIAL_APPLY_ALL_VAST, blockCategories: new Set() };
        if (zoneType === "publica") return { ...INITIAL_APPLY_ALL_PUBLICA };
        if (zoneType === "GAM") return { ...INITIAL_APPLY_ALL_GAM, sizes: [], companion: [] };
        return { ...INITIAL_APPLY_ALL_PREBID, blockCategories: new Set() };
    };

    const [applyAllValues, setApplyAllValues] = useState(() => getInitialApplyAll());
    const [rowsData, setRowsData] = useState([]);
    const [rowErrors, setRowErrors] = useState({});   // { rowKey: { field: "error msg" } }
    // const [applyAllErrors, setApplyAllErrors] = useState({});  // { field: "error msg" }
    const [buttonProcessing, setButtonProcessing] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);

    // ── Bid Adjustment auto-calculation (matches ZoneViewEdit formula) ─────────
    const calcBidAdjustment = (ecpmValue) => {
        const n = Number(ecpmValue);
        if (isNaN(n) || ecpmValue === "" || ecpmValue === null || ecpmValue === undefined) return "";
        return String(Math.round((n * 100) / (n + 2.5)));
    };

    // Exchange type options (fetched from API) — used to map value → label
    const [exchangeTypeOptions, setExchangeTypeOptions] = useState([
        { label: "OpenRTB (Both ADM and nURL)", value: "OPEN_RTB" },
        { label: "ADM (OpenRtb w/o nurl)", value: "OPEN_RTB_WO_NURL" },
        { label: "nURL (OpenRtb nurl only)", value: "OPEN_RTB_NURL_ONLY" },
    ]);

    // Fetch exchange type options
    useEffect(() => {

        if (zoneType !== "Open RTB") return;
        axios
            .get(configData.SERVER_URL + "ops/getExchangeTypes", {
                headers: { "Content-Type": "application/json" },
            })
            .then((response) => {
                if (response.data.data) setExchangeTypeOptions(response.data.data);
            })
            .catch(() => { });
    }, [zoneType]);

    // Template options (Vast Zone ADK) — keyed by platform type: { Website: [...], Inapp: [...], CTV: [...] }
    const [templateOptions, setTemplateOptions] = useState({});

    // Template options (Publica) — flat array from publica/templates
    const [publicaTemplateOptions, setPublicaTemplateOptions] = useState([]);

    useEffect(() => {
        if (zoneType !== "Vast Zone" && zoneType !== "publica") return;
        const token = Cookies.get("token_xapads_ssp");
        if (!token) return;

        if (zoneType === "Vast Zone") {
            axios
                .get(configData.SERVER_URL + "zone/getAdKTempList", {
                    headers: { "Content-Type": "application/json", "x-access-token": token },
                })
                .then((response) => {
                    const data = response.data;
                    if (data && typeof data === "object") {
                        const opts = {};
                        ["Website", "Inapp", "CTV"].forEach((key) => {
                            if (Array.isArray(data[key])) opts[key] = data[key];
                        });
                        setTemplateOptions(opts);
                    }
                })
                .catch(() => { });
        }

        if (zoneType === "publica") {
            axios
                .get(configData.SERVER_URL + "publica/templates", {
                    headers: { "Content-Type": "application/json" },
                })
                .then((response) => {
                    if (Array.isArray(response.data?.data)) {
                        setPublicaTemplateOptions(response.data.data);
                    }
                })
                .catch(() => { });
        }
    }, [zoneType]);

    // ── GAM: option lists + multi-select dropdown infra ───────────────────────
    const [tvApplicationOptions_gam, setTvApplicationOptions_gam] = useState([]);
    const [uprCategoryOptions_gam, setUprCategoryOptions_gam] = useState([]);
    const [gamSearch, setGamSearch] = useState("");
    const [openSelectState_gam, setOpenSelectState_gam] = useState({});
    // Zone-name uniqueness check (per row, all zone types): hits gam-new/zone-name/check; response.data.nameExists === true => name already exists
    const [gamZoneNameExists, setGamZoneNameExists] = useState({});
    // per-row: true while that row's uniqueness API call is in flight (drives the input suffix spinner)
    const [gamZoneNameChecking, setGamZoneNameChecking] = useState({});
    const gamZoneNameCheckTimers = useRef({});
    const gamZoneNameCheckSeq = useRef({});
    const gamZoneNameAbort = useRef({});
    const refsMap_gam = useRef({});
    const getRef_gam = (key) => {
        if (!refsMap_gam.current[key]) refsMap_gam.current[key] = React.createRef();
        return refsMap_gam.current[key];
    };
    const toggleSelectOpen_gam = (key, open) => {
        setOpenSelectState_gam((prev) => (prev[key] === open ? prev : { ...prev, [key]: open }));
        if (open) {
            setTimeout(() => refsMap_gam.current[key]?.current?.focus(), 300);
        } else {
            setGamSearch("");
        }
    };

    useEffect(() => {
        if (zoneType !== "GAM") return;
        const token = Cookies.get("token_xapads_ssp");
        if (!token) return;
        const headers = { "Content-Type": "application/json", "x-access-token": token };

        // CTV applications (label: "displayName (appStoreId)", value: id)
        axios
            .get(configData.SERVER_URL + "gam-new/ctv-applications", { headers })
            .then((response) => {
                const apps = response.data?.data || [];
                setTvApplicationOptions_gam(
                    apps.map((app) => ({ label: `${app.displayName} (${app.appStoreId})`, value: app.id }))
                );
            })
            .catch(() => { });

        // UPR placements (label: upr_name, value: upr_id, expected: expected_amount_usd)
        axios
            .get(configData.SERVER_URL + "upr-placement/", { headers })
            .then((response) => {
                const uprs = response.data?.data || [];
                setUprCategoryOptions_gam(
                    uprs.map((upr) => ({ label: upr.upr_name, value: upr.upr_id, expected: upr.expected_amount_usd }))
                );
            })
            .catch(() => { });
    }, [zoneType]);

    // Match a value against UPR Category options' expected_amount_usd.
    //  - returns { value, expected } for the smallest expected >= value (exact or nearest greater)
    //  - returns { notFound: true } when every expected is smaller than the value
    //  - returns null when the value / options are unusable
    const matchUprCategory_gam = (rawValue) => {
        const num = parseFloat(rawValue);
        const candidates = (uprCategoryOptions_gam || [])
            .filter((o) => typeof o.expected === "number" && !isNaN(o.expected))
            .sort((a, b) => a.expected - b.expected);
        if (isNaN(num) || candidates.length === 0) return null;
        const chosen = candidates.find((o) => o.expected >= num);
        if (!chosen) return { notFound: true };
        return { value: chosen.value, expected: Number(chosen.expected).toFixed(2) };
    };

    // True when the row's selected UPR Category differs from the one its Set UPR (row.upr)
    // value implies — surfaces the "does not match the price" alert (mirrors ZoneViewEdit).
    const isUprCategoryMismatch_gam = (row) => {
        if (!row || !row.uprCategory) return false;
        const num = parseFloat(row.upr);
        if (isNaN(num)) return false;
        const candidates = (uprCategoryOptions_gam || [])
            .filter((o) => typeof o.expected === "number" && !isNaN(o.expected))
            .sort((a, b) => a.expected - b.expected);
        if (candidates.length === 0) return false;
        const expectedCat = candidates.find((o) => o.expected >= num);
        if (!expectedCat) return false; // no match -> handled by the "not found" error instead
        return row.uprCategory !== expectedCat.value;
    };

    // Patch a single row, marking the given fields as manual overrides
    const gamSetRowFields = (rowKey, updates, markOverride = []) => {
        setRowsData((prev) =>
            prev.map((row) => {
                if (row.key !== rowKey) return row;
                const overrides = new Set(row.manualOverrideFields);
                markOverride.forEach((f) => overrides.add(f));
                return { ...row, ...updates, manualOverrideFields: overrides };
            })
        );
    };

    // Apply an Apply-All transform to every row, clearing the given override flags
    const gamApplyAllRows = (fieldsToReset, transform) => {
        setRowsData((prev) =>
            prev.map((row) => {
                const overrides = new Set(row.manualOverrideFields);
                fieldsToReset.forEach((f) => overrides.delete(f));
                const updates = transform(row) || {};
                return { ...row, ...updates, manualOverrideFields: overrides };
            })
        );
    };

    const clearRowError_gam = (rowKey, ...fields) => {
        setRowErrors((prev) => {
            const e = { ...(prev[rowKey] || {}) };
            fields.forEach((f) => { e[f] = null; });
            return { ...prev, [rowKey]: e };
        });
    };

    // ── GAM: Override UPR / Override Set UPR / UPR Category (per-row) ──────────
    const computeOverrideUprToggle_gam = (row, checked) => {
        const updates = { overrideUpr: checked };
        if (checked) {
            let driving = row.overrideSetUpr;
            if (driving === "" || isNaN(parseFloat(driving))) {
                if (row.upr !== "" && !isNaN(parseFloat(row.upr))) {
                    driving = row.upr;
                    updates.overrideSetUpr = row.upr;
                }
            }
            const res = (driving !== "" && !isNaN(parseFloat(driving))) ? matchUprCategory_gam(driving) : null;
            if (res && !res.notFound) {
                updates.uprCategory = res.value;
                // Override Set UPR keeps its value; only the category reflects the nearest-ceiling match
                updates.uprCategoryNotFound = false;
                updates.uprCategoryMatched = true;
            } else if (res && res.notFound) {
                updates.uprCategory = "";
                updates.uprCategoryNotFound = true;
                updates.uprCategoryMatched = false;
            }
        } else {
            const res = (row.upr !== "" && !isNaN(parseFloat(row.upr))) ? matchUprCategory_gam(row.upr) : null;
            if (res && !res.notFound) {
                updates.uprCategory = res.value;
                updates.uprCategoryNotFound = false;
                updates.uprCategoryMatched = true;
            } else if (res && res.notFound) {
                updates.uprCategory = "";
                updates.uprCategoryNotFound = true;
                updates.uprCategoryMatched = false;
            }
        }
        return updates;
    };

    const handleGamOverrideUprToggle = (rowKey, checked) => {
        setRowsData((prev) =>
            prev.map((row) => {
                if (row.key !== rowKey) return row;
                const overrides = new Set(row.manualOverrideFields);
                ["overrideUpr", "overrideSetUpr", "uprCategory"].forEach((f) => overrides.add(f));
                return { ...row, ...computeOverrideUprToggle_gam(row, checked), manualOverrideFields: overrides };
            })
        );
        if (!checked) clearRowError_gam(rowKey, "overrideSetUpr");
    };

    const handleGamOverrideSetUprChange = (rowKey, v) => {
        if (v !== "" && !/^\d*\.?\d*$/.test(v)) return;
        gamSetRowFields(rowKey, { overrideSetUpr: v, uprCategoryNotFound: false, uprCategoryMatched: false }, ["overrideSetUpr"]);
        clearRowError_gam(rowKey, "overrideSetUpr");
    };

    const handleGamOverrideSetUprBlur = (rowKey, raw) => {
        const value = String(raw).trim();
        setRowErrors((prev) => {
            const e = { ...(prev[rowKey] || {}) };
            if (value === "") e.overrideSetUpr = "This field is required";
            else if (isNaN(parseFloat(value)) || parseFloat(value) <= 0) e.overrideSetUpr = "Value must be greater than 0";
            else e.overrideSetUpr = null;
            return { ...prev, [rowKey]: e };
        });
        if (value === "" || isNaN(parseFloat(value)) || parseFloat(value) <= 0) return;
        const res = matchUprCategory_gam(value);
        if (!res) return;
        if (res.notFound) {
            gamSetRowFields(rowKey, { uprCategory: "", uprCategoryNotFound: true, uprCategoryMatched: false }, ["uprCategory"]);
            clearRowError_gam(rowKey, "uprCategory");
        } else {
            // Override Set UPR keeps its entered value; only the category reflects the nearest-ceiling match
            gamSetRowFields(rowKey, { uprCategory: res.value, uprCategoryNotFound: false, uprCategoryMatched: true }, ["uprCategory"]);
            clearRowError_gam(rowKey, "uprCategory", "overrideSetUpr");
        }
    };

    const handleGamUprCategoryChange = (rowKey, value) => {
        const selected = (uprCategoryOptions_gam || []).find((o) => o.value === value);
        const expectedStr = (selected && typeof selected.expected === "number" && !isNaN(selected.expected))
            ? Number(selected.expected).toFixed(2) : null;
        setRowsData((prev) =>
            prev.map((row) => {
                if (row.key !== rowKey) return row;
                const overrides = new Set(row.manualOverrideFields);
                overrides.add("uprCategory");
                const updates = { uprCategory: value, uprCategoryNotFound: false, uprCategoryMatched: !!value };
                if (expectedStr !== null) {
                    if (row.overrideUpr) { updates.overrideSetUpr = expectedStr; overrides.add("overrideSetUpr"); }
                    else { updates.upr = expectedStr; }
                }
                return { ...row, ...updates, manualOverrideFields: overrides };
            })
        );
        clearRowError_gam(rowKey, "uprCategory", "overrideSetUpr");
    };

    // ── GAM: Override UPR / Set UPR / UPR Category (Apply-All) ─────────────────
    const handleGamApplyAllOverrideUprToggle = (checked) => {
        setApplyAllValues((prev) => ({ ...prev, overrideUpr: checked }));
        gamApplyAllRows(["overrideUpr", "overrideSetUpr", "uprCategory"], (row) => computeOverrideUprToggle_gam(row, checked));
    };

    const handleGamApplyAllOverrideSetUprChange = (v) => {
        if (v !== "" && !/^\d*\.?\d*$/.test(v)) return;
        setApplyAllValues((prev) => ({ ...prev, overrideSetUpr: v }));
        gamApplyAllRows(["overrideSetUpr"], () => ({ overrideSetUpr: v, uprCategoryNotFound: false, uprCategoryMatched: false }));
    };

    const handleGamApplyAllOverrideSetUprBlur = (raw) => {
        const value = String(raw).trim();
        if (value === "" || isNaN(parseFloat(value)) || parseFloat(value) <= 0) return;
        const res = matchUprCategory_gam(value);
        if (!res) return;
        gamApplyAllRows(["uprCategory"], () => {
            if (res.notFound) return { uprCategory: "", uprCategoryNotFound: true, uprCategoryMatched: false };
            // Override Set UPR keeps its entered value; only the category reflects the nearest-ceiling match
            return { uprCategory: res.value, uprCategoryNotFound: false, uprCategoryMatched: true };
        });
    };

    const handleGamApplyAllUprCategoryChange = (value) => {
        setApplyAllValues((prev) => ({ ...prev, uprCategory: value }));
        const selected = (uprCategoryOptions_gam || []).find((o) => o.value === value);
        const expectedStr = (selected && typeof selected.expected === "number" && !isNaN(selected.expected))
            ? Number(selected.expected).toFixed(2) : null;
        gamApplyAllRows(["uprCategory", "overrideSetUpr"], (row) => {
            const updates = { uprCategory: value, uprCategoryNotFound: false, uprCategoryMatched: !!value };
            if (expectedStr !== null) {
                if (row.overrideUpr) updates.overrideSetUpr = expectedStr;
                else updates.upr = expectedStr;
            }
            return updates;
        });
    };

    // Block-category modal
    const [blockCatModalOpen, setBlockCatModalOpen] = useState(false);
    const [blockCatModalRowKey, setBlockCatModalRowKey] = useState(null);
    const [blockCatModalValue, setBlockCatModalValue] = useState(new Set());
    const [blockCatNormalizeKey, setBlockCatNormalizeKey] = useState(0);

    // ── Load zone details: single bulk API call, fall back to sample ────────────
    useEffect(() => {
        const token = Cookies.get("token_xapads_ssp");

        // const getSampleDetail = (zone, idx) => {
        //     if (zoneType === "Open RTB") return generateSampleOpenRTBZoneDetail(zone, idx);
        //     if (zoneType === "Vast Zone") return generateSampleVastZoneDetail(zone, idx);
        //     if (zoneType === "publica") return generateSamplePublicaZoneDetail(zone, idx);
        //     return generateSamplePrebidZoneDetail(zone, idx);
        // };

        const buildRow = (detail, idx) => {
            if (zoneType === "Open RTB") return buildOpenRTBRow(detail, idx);
            if (zoneType === "Vast Zone") return buildVastZoneRow(detail, idx);
            if (zoneType === "publica") return buildPublicaRow(detail, idx);
            if (zoneType === "GAM") return buildGamRow(detail, idx);
            return buildPrebidRow(detail, idx);
        };

        // const loadFallback = () => {
        //     setRowsData(selectedZones.map((z, i) => buildRow(getSampleDetail(z, i), i)));
        //     setDataLoading(false);
        // };

        if (!token || selectedZones.length === 0) {
            // loadFallback();
            return;
        }

        const z_Ids = selectedZones.map((z) => String(z.z_Id || z.id));

        axios
            .post(
                configData.SERVER_URL + "zone/bulkZoneDetails",
                { z_Ids },
                { headers: { "Content-Type": "application/json", "x-access-token": token } }
            )
            .then((res) => {
                const apiZones = res.data.data || []; // array of zone detail objects
                // Build a lookup by z_Id for easy matching
                const detailMap = {};
                apiZones.forEach((d) => { if (d.z_Id) detailMap[d.z_Id] = d; });

                setRowsData(
                    selectedZones.map((zone, idx) => {
                        const apiData = detailMap[zone.z_Id || zone.id];
                        if (apiData) {
                            return buildRow(
                                { ...apiData, publisher_id: zone.p_adkId || apiData.publisher_id || "" },
                                idx
                            );
                        }
                        // Zone not returned by API → use sample fallback
                        // return buildRow(getSampleDetail(zone, idx), idx);
                        notifyfailure("Failed to fetch api data.")
                        return null;
                    }).filter(Boolean)
                );

                setDataLoading(false);
                setFormVisible(true)
            })
            .catch(() => {
                // API down → load sample data for all zones
                // loadFallback();

                notifyfailure("Failed to fetch api data.")

                setDataLoading(false);
                setFormVisible(true)
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Apply-to-All: propagate to ALL rows, reset override, and validate ────
    const handleApplyAllChange = (field, value) => {
        setApplyAllValues((prev) => ({ ...prev, [field]: value }));
        setRowsData((prev) =>
            prev.map((row) => {
                const overrides = new Set(row.manualOverrideFields);
                overrides.delete(field);
                return { ...row, [field]: value, manualOverrideFields: overrides };
            })
        );
        // Run validation and propagate errors to all data rows (not Apply All row itself)
        const validators = getFieldValidators();
        if (validators[field]) {
            const err = validators[field](value);
            setRowErrors((prev) => {
                const updated = { ...prev };
                rowsData.forEach((row) => {
                    updated[row.key] = { ...(updated[row.key] || {}), [field]: err };
                });
                return updated;
            });
        }
    };

    // ── Get validators for current zone type ────────────────────────────────────
    const getFieldValidators = () => {
        if (zoneType === "Open RTB") return OPENRTB_FIELD_VALIDATORS;
        if (zoneType === "Vast Zone") return VAST_FIELD_VALIDATORS;
        if (zoneType === "publica") return PUBLICA_FIELD_VALIDATORS;
        if (zoneType === "GAM") return GAM_FIELD_VALIDATORS;
        return PREBID_FIELD_VALIDATORS;
    };

    // ── Manual row edit with inline validation ─────────────────────────────────
    const handleRowChange = (rowKey, field, value) => {
        setRowsData((prev) =>
            prev.map((row) => {
                if (row.key !== rowKey) return row;
                const overrides = new Set(row.manualOverrideFields);
                overrides.add(field);
                return { ...row, [field]: value, manualOverrideFields: overrides };
            })
        );
        const validators = getFieldValidators();
        if (validators[field]) {
            const err = validators[field](value);
            setRowErrors((prev) => ({
                ...prev,
                [rowKey]: { ...(prev[rowKey] || {}), [field]: err },
            }));
        }
    };

    // ── Per-row zone-name uniqueness check (all zone types) ────────────────────
    // Hit the API for a single row's name; response.data.nameExists === true => the name already exists.
    // A per-row sequence guard ignores out-of-order responses from rapid edits.
    const checkGamZoneNameUnique = (rowKey, rawName) => {
        const name = (rawName || "").trim();
        if (name === "" || name.length > 90) {
            setGamZoneNameExists((p) => ({ ...p, [rowKey]: false }));
            setGamZoneNameChecking((p) => ({ ...p, [rowKey]: false }));
            return;
        }
        const token = Cookies.get("token_xapads_ssp");
        if (!token) return;
        const zoneId = rowsData.find((r) => r.key === rowKey)?.zoneId;
        // cancel any in-flight check for this row so only the latest request resolves
        if (gamZoneNameAbort.current[rowKey]) gamZoneNameAbort.current[rowKey].abort();
        const controller = new AbortController();
        gamZoneNameAbort.current[rowKey] = controller;
        const seq = (gamZoneNameCheckSeq.current[rowKey] || 0) + 1;
        gamZoneNameCheckSeq.current[rowKey] = seq;
        setGamZoneNameChecking((p) => ({ ...p, [rowKey]: true }));
        axios
            .get(configData.SERVER_URL + "zone/zone-name/check", {
                params: { name, zoneId },
                headers: { "x-access-token": token },
                signal: controller.signal,
            })
            .then((response) => {
                if (gamZoneNameCheckSeq.current[rowKey] !== seq) return;
                setGamZoneNameExists((p) => ({ ...p, [rowKey]: response.data?.nameExists === true }));
                setGamZoneNameChecking((p) => ({ ...p, [rowKey]: false }));
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                if (gamZoneNameCheckSeq.current[rowKey] !== seq) return;
                setGamZoneNameExists((p) => ({ ...p, [rowKey]: false }));
                setGamZoneNameChecking((p) => ({ ...p, [rowKey]: false }));
            });
    };

    // Debounced wrapper used while the user is typing a GAM row's zone name
    const debouncedCheckGamZoneName = (rowKey, name) => {
        if (gamZoneNameCheckTimers.current[rowKey]) clearTimeout(gamZoneNameCheckTimers.current[rowKey]);
        gamZoneNameCheckTimers.current[rowKey] = setTimeout(() => checkGamZoneNameUnique(rowKey, name), 500);
    };

    // ── Block-category modal ───────────────────────────────────────────────────
    const openBlockCatModal = (rowKey) => {
        const current =
            rowKey === "apply-all"
                ? new Set(applyAllValues.blockCategories)
                : new Set(rowsData.find((r) => r.key === rowKey)?.blockCategories || []);
        setBlockCatModalRowKey(rowKey);
        setBlockCatModalValue(current);
        setBlockCatNormalizeKey((k) => k + 1);
        setBlockCatModalOpen(true);
    };

    const applyBlockCat = () => {
        const val = new Set(blockCatModalValue);
        if (blockCatModalRowKey === "apply-all") handleApplyAllChange("blockCategories", val);
        else handleRowChange(blockCatModalRowKey, "blockCategories", val);
        setBlockCatModalOpen(false);
    };

    // ── Count filled editable values (zone-type-aware) ────────────────────────
    const getEditableFields = () => {
        if (zoneType === "Open RTB") return OPENRTB_EDITABLE_FIELDS;
        if (zoneType === "Vast Zone") return VAST_EDITABLE_FIELDS;
        if (zoneType === "publica") return PUBLICA_EDITABLE_FIELDS;
        if (zoneType === "GAM") return GAM_EDITABLE_FIELDS;
        return PREBID_EDITABLE_FIELDS;
    };

    const countValuesSet = () =>
        rowsData.reduce((total, row) =>
            total + getEditableFields().filter((f) => {
                const v = row[f];
                if (f === "blockCategories") return v instanceof Set && v.size > 0;
                if (Array.isArray(v)) return v.length > 0;
                if (f === "passIPD" || f === "isTargetEcpm" || f === "overrideUpr") return v === true;
                return v !== "" && v !== null && v !== undefined;
            }).length
            , 0);

    // ── Prebid: validate all rows before submit ─────────────────────────────────
    const validatePrebidRows = () => {
        const allErrors = {};
        let hasErrors = false;
        rowsData.forEach((row) => {
            const errors = {};
            Object.keys(PREBID_FIELD_VALIDATORS).forEach((field) => {
                const err = PREBID_FIELD_VALIDATORS[field](row[field]);
                if (err) { errors[field] = err; hasErrors = true; }
            });
            if (Object.keys(errors).length > 0) allErrors[row.key] = errors;
        });
        setRowErrors(allErrors);
        return !hasErrors;
    };

    // ── Open RTB: validate all rows before submit ─────────────────────────────
    const validateOpenRTBRows = () => {
        const allErrors = {};
        let hasErrors = false;
        rowsData.forEach((row) => {
            const errors = {};
            Object.keys(OPENRTB_FIELD_VALIDATORS).forEach((field) => {
                const err = OPENRTB_FIELD_VALIDATORS[field](row[field]);
                if (err) { errors[field] = err; hasErrors = true; }
            });
            if (Object.keys(errors).length > 0) allErrors[row.key] = errors;
        });
        setRowErrors(allErrors);
        return !hasErrors;
    };

    // ── Vast Zone: validate all rows before submit ─────────────────────────
    const validateVastRows = () => {
        const allErrors = {};
        let hasErrors = false;
        rowsData.forEach((row) => {
            const errors = {};
            // Always validate human & pixalate
            Object.keys(VAST_FIELD_VALIDATORS).forEach((field) => {
                const err = VAST_FIELD_VALIDATORS[field](row[field]);
                if (err) { errors[field] = err; hasErrors = true; }
            });
            // Conditionally validate targetEcpm only when isTargetEcpm is checked
            if (row.isTargetEcpm) {
                const err = SHARED_FIELD_VALIDATORS.targetEcpm(row.targetEcpm);
                if (err) { errors.targetEcpm = err; hasErrors = true; }
            }
            if (Object.keys(errors).length > 0) allErrors[row.key] = errors;
        });
        setRowErrors(allErrors);
        return !hasErrors;
    };

    // ── Publica: validate all rows before submit ─────────────────────────
    const validatePublicaRows = () => {
        const allErrors = {};
        let hasErrors = false;
        rowsData.forEach((row) => {
            const errors = {};
            Object.keys(PUBLICA_FIELD_VALIDATORS).forEach((field) => {
                const err = PUBLICA_FIELD_VALIDATORS[field](row[field]);
                if (err) { errors[field] = err; hasErrors = true; }
            });
            // Conditionally validate targetEcpm only when isTargetEcpm is checked
            if (row.isTargetEcpm) {
                const err = SHARED_FIELD_VALIDATORS.targetEcpm(row.targetEcpm);
                if (err) { errors.targetEcpm = err; hasErrors = true; }
            }
            if (Object.keys(errors).length > 0) allErrors[row.key] = errors;
        });
        setRowErrors(allErrors);
        return !hasErrors;
    };

    // ── GAM: validate all rows before submit ──────────────────────────────────
    const validateGamRows = () => {
        const allErrors = {};
        let hasErrors = false;
        rowsData.forEach((row) => {
            const errors = {};
            const zn = GAM_FIELD_VALIDATORS.zoneName(row.zoneName);
            if (zn) errors.zoneName = zn;
            else if (gamZoneNameExists[row.key]) errors.zoneName = "Zone name already exists";
            const sz = GAM_FIELD_VALIDATORS.sizes(row.sizes);
            if (sz) errors.sizes = sz;
            // Override Set UPR is mandatory (> 0) only when Override UPR is checked
            if (row.overrideUpr) {
                const v = String(row.overrideSetUpr ?? "").trim();
                if (v === "") errors.overrideSetUpr = "This field is required";
                else if (isNaN(parseFloat(v)) || parseFloat(v) <= 0) errors.overrideSetUpr = "Value must be greater than 0";
            }
            // UPR Category required; a "not found" match blocks submit too
            if (row.uprCategoryNotFound) errors.uprCategory = "UPR Category as per UPR not found";
            else if (!row.uprCategory) errors.uprCategory = "This field is required";
            // TV Application required when CTV + TV app name checkbox
            if (row.trafficType === "CTV" && row.tvAppCheckbox && !row.tvApplication) {
                errors.tvApplication = "This field is required";
            }
            if (Object.keys(errors).length > 0) { allErrors[row.key] = errors; hasErrors = true; }
        });
        setRowErrors(allErrors);
        return !hasErrors;
    };

    // ── Dispatch to zone-type-specific validator ──────────────────────────────
    const validateAllRows = () => {
        let typeValid;
        if (zoneType === "Open RTB") typeValid = validateOpenRTBRows();
        else if (zoneType === "Vast Zone") typeValid = validateVastRows();
        else if (zoneType === "publica") typeValid = validatePublicaRows();
        else if (zoneType === "GAM") typeValid = validateGamRows();
        else typeValid = validatePrebidRows();
        // universal: block submit if any row's name is a known duplicate (all zone types)
        const anyDuplicateName = rowsData.some((r) => gamZoneNameExists[r.key]);
        return typeValid && !anyDuplicateName;
    };

    // ── Prebid: payload builder (matches ZoneViewEdit editPrebidZone payload) ──
    const buildPrebidPayload = (row) => {
        const payload = {
            zoneId: row.zoneId,
            status: row.status,
            name: row.zoneName,
            remoteIds: [],
            revshare: row.revShare,
            minEcpm: row.minEcpm,
            maxEcpm: row.maxEcpm,
            isHighestBidFloor: (row.minEcpm === "" || row.minEcpm === null) ? false : row.isHighestBidFloor,
            sampling_human: Number(row.human),
            // PIXALATE SUNSET: not sent in payload
            // sampling_pixalate: Number(row.pixalate),
            sampling_dv: Number(row.doubleverify),
            blockCategory: [...(row.blockCategories || [])],
        };
        payload.ipd_bool = row.passIPD;
        payload.ipd_value = row.partnerDomain;
        return payload;
    };

    // ── Open RTB: payload builder (matches ZoneViewEdit editORTBZone payload) ──
    const buildOpenRTBPayload = (row) => {
        const payload = {
            zoneId: row.zoneId,
            status: row.status,
            name: row.zoneName,
            remoteIds: [],
            revshare: row.revShare,
            minEcpm: row.minEcpm,
            maxEcpm: row.maxEcpm,
            isHighestBidFloor: (row.minEcpm === "" || row.minEcpm === null) ? false : row.isHighestBidFloor,
            sampling_human: Number(row.human),
            // PIXALATE SUNSET: not sent in payload
            // sampling_pixalate: Number(row.pixalate),
            sampling_dv: Number(row.doubleverify),
            blockCategory: [...(row.blockCategories || [])],
        };
        payload.ipd_bool = row.passIPD;
        payload.ipd_value = row.partnerDomain;
        return payload;
    };

    // ── Vast Zone: payload builder (matches ZoneViewEdit editVastZone payload) ──
    const buildVastPayload = (row) => {
        const payload = {
            zoneId: row.zoneId,
            status: row.status,
            name: row.zoneName,
            remoteIds: [],
            sampling_human: Number(row.human),
            // PIXALATE SUNSET: not sent in payload
            // sampling_pixalate: Number(row.pixalate),
            sampling_dv: Number(row.doubleverify),
            blockCategory: [...(row.blockCategories || [])],
        };
        payload.ipd_bool = row.passIPD;
        payload.ipd_value = row.partnerDomain;
        if (row.template) {
            payload.template = row.template;
        }
        payload.isTargetEcpm = row.isTargetEcpm;
        payload.target_ecpm = Number(row.targetEcpm);
        return payload;
    };

    // ── Publica: payload builder (matches ZoneViewEdit editPublicaZone payload) ──
    const buildPublicaPayload = (row) => {
        const payload = {
            zoneId: row.zoneId,
            status: row.status,
            formType: row.formatType ? row.formatType.join(",") : "",
            invent_type: row.trafficType,
            header_bidder_vast_version: row.hbVastVersion,
            channel_name: row.channelName,
            region: row.region,
            eCpm: row.eCpm,
            bid_adjustment: String(row.bidAdjustment),
            bid_adjustment_value: String(row.bidAdjustmentValue),
            asi: row.asi,
            sid: row.sid,
            name: row.scName,
            domain: row.scDomain,
            isAddValueCheckbox: row.passIPD,
            override: row.partnerDomain,
            template: row.template,
            sampling_human: Number(row.human),
            sampling_pixalate: Number(row.pixalate),
            sampling_dv: Number(row.doubleverify),
            isTargetEcpm: row.isTargetEcpm,
            target_ecpm: Number(row.targetEcpm),
        };
        return payload;
    };

    // ── GAM: payload builder (matches ZoneViewEdit gam-new/adunit payload) ─────
    const buildGamPayload = (row) => {
        const isVideo = (row.trafficType === "CTV" ? "Video" : row.formatType) === "Video";
        const payload = {
            zoneId: row.zoneId,
            formType: "GAM",
            invent_type: row.trafficType,
            traffic_type: row.formatType,
            name: row.zoneName,
            status: row.status,
            // is_active: row.status === "enabled",
            sizes: {
                bannerSizes: !isVideo ? row.sizes : [],
                vastSizes: isVideo ? row.sizes : [],
                companionSizes: isVideo ? row.companion : [],
            },
            is_tv_app_name: row.trafficType === "CTV" ? row.tvAppCheckbox : false,
            applicationId: row.trafficType === "CTV" && row.tvAppCheckbox ? row.tvApplication : "",
            isTargetEcpm: row.overrideUpr,
            fixed_cpm: row.fixedPrice !== "" ? Number(row.fixedPrice) : 0,
            set_upr: row.upr !== "" ? Number(row.upr) : 0,
            target_ecpm: row.overrideUpr && row.overrideSetUpr !== ""
                ? Number(row.overrideSetUpr) : Number(row.upr),
            upr_id: row.uprCategory,
        };
        return payload;
    };

    const buildPayload = (row) => {
        if (zoneType === "Open RTB") return buildOpenRTBPayload(row);
        if (zoneType === "Vast Zone") return buildVastPayload(row);
        if (zoneType === "publica") return buildPublicaPayload(row);
        if (zoneType === "GAM") return buildGamPayload(row);
        return buildPrebidPayload(row);
    };

    // ── Save ───────────────────────────────────────────────────────────────────
    const handleSave = () => {
        if (!validateAllRows()) {
            notifyfailure("Please fix validation errors before saving.");
            return;
        }
        const token = Cookies.get("token_xapads_ssp");
        if (!token) return;

        const zones = rowsData.map((row) => buildPayload(row));
        setButtonProcessing(true);
        // console.log(zones)
        // return
        axios
            .post(
                configData.SERVER_URL + "zone/editBulkZone",
                { zoneType, zones },
                { headers: { "Content-Type": "application/json", "x-access-token": token } }
            )
            .then(() => {
                notifysuccess(`${zones.length} zone${zones.length !== 1 ? "s" : ""} updated successfully.`);
                handlePageViewChange("table", "yes");
            })
            .catch((error) => {
                notifyfailure(
                    error?.response?.data?.message || "Failed to update zones. Please try again."
                );
            })
            .finally(() => setButtonProcessing(false));
    };

    // ─── Shared column builders ────────────────────────────────────────────────

    // Zone Name – fixed left, editable input (shared by Prebid, Open RTB, Vast Zone)
    const makeZoneNameColumn = () => ({
        title: "Zone Name",
        dataIndex: "zoneName",
        width: 350,
        fixed: "left",
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            if (isApplyAll) {
                return (
                    <div className="apply_all_row">
                        <span className="dot"></span>
                        <span className="apply_text">Apply to All</span>
                        <span className="apply_desc">fills all zones</span>
                    </div>
                );
            }
            const applyAllHasValue = applyAllValues.zoneName !== "" && applyAllValues.zoneName !== null;
            const isOverridden = applyAllHasValue && value !== applyAllValues.zoneName;
            const error = rowErrors[record.key]?.zoneName;
            // flag when the name already exists (per-row uniqueness check, all zone types)
            const nameExists = gamZoneNameExists[record.key];
            const nameChecking = gamZoneNameChecking[record.key];
            return (
                <div className="form_content_block--group">
                    <Input
                        value={value}
                        className={`form_control basic${isOverridden ? " change_dot_cls" : ""}${error || nameExists ? " error" : ""}`}
                        placeholder="-"
                        suffix={nameChecking ? <img src={loaderIcon} alt="" style={{ width: 16, height: 16 }} /> : <span />}
                        onChange={(e) => {
                            handleRowChange(record.key, "zoneName", e.target.value);
                            setGamZoneNameExists((p) => ({ ...p, [record.key]: false }));
                            debouncedCheckGamZoneName(record.key, e.target.value);
                        }}
                        onBlur={(e) => {
                            if (gamZoneNameCheckTimers.current[record.key]) clearTimeout(gamZoneNameCheckTimers.current[record.key]);
                            checkGamZoneNameUnique(record.key, e.target.value);
                        }}
                    />
                    <div className="zone_id mt-2">{record.zoneId}</div>
                    {nameExists && <span className="error_content">Zone name already exists</span>}
                </div>
            );
        },
    });

    // Status dropdown – editable (shared)
    const makeStatusColumn = () => ({
        title: "Status",
        dataIndex: "status",
        width: 155,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues.status : value;
            const applyAllHasValue = applyAllValues.status !== null && applyAllValues.status !== undefined;
            const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.status;
            return (
                <Select
                    className={`form_select basic ${isOverridden ? "change_dot_cls " : ""}${isApplyAll
                        ? "editable_select_tag"
                        : val === "enabled" ?
                            "editable_select_tag badge_content--approved"
                            : val === "disabled" ?
                                "editable_select_tag badge_content--declined"
                                : "editable_select_tag badge_content--archived"}`}
                    style={{ width: isApplyAll ? "100%" : "" }}
                    value={val || undefined}
                    dropdownStyle={{ width: "13rem" }}
                    placeholder={isApplyAll ? "" : undefined}
                    suffixIcon={<ChevronDown />}
                    onChange={(v) => {
                        if (isApplyAll) handleApplyAllChange("status", v);
                        else handleRowChange(record.key, "status", v);
                    }}
                    options={[
                        { label: "Enabled", value: "enabled" },
                        { label: "Disabled", value: "disabled" },
                        { label: "Archived", value: "archived" },
                    ]}
                />
            );
        },
    });

    // Non-editable label column factory (shared) – Apply-All row shows nothing
    const makeLabelCol = (title, dataIndex, width, renderCell) => ({
        title,
        dataIndex,
        width,
        render: (value, record) => {
            if (record.isApplyAll) return null;
            if (renderCell) return renderCell(value, record);
            const display = value !== "" && value !== undefined && value !== null
                ? String(value) : "—";
            return <span className="table_label_text">{display}</span>;
        },
    });

    // Editable input column factory – with inline red validation (shared)
    const makeInputCol = (title, field, width, prefix, suffix, numericOnly) => ({
        title,
        dataIndex: field,
        width,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues[field] : value;
            const applyAllHasValue =
                applyAllValues[field] !== "" && applyAllValues[field] !== null && applyAllValues[field] !== undefined;
            const isOverridden =
                !isApplyAll && applyAllHasValue && value !== applyAllValues[field];
            const error = isApplyAll ? null : rowErrors[record.key]?.[field];
            const inputProps = {};
            if (prefix) inputProps.prefix = prefix;
            if (suffix) inputProps.suffix = suffix;
            return (
                <div className={`form_content_block--group${isOverridden ? " change_dot_cls" : ""}`}>
                    <Input
                        {...inputProps}
                        value={val}
                        className={`form_control basic${prefix ? " prefix_sign" : ""}${suffix ? " subfix_sign" : ""}${error ? " error" : ""}`}
                        placeholder="-"
                        onChange={(e) => {
                            const v = e.target.value;
                            if (numericOnly === "integer" && v !== "" && !/^\d*$/.test(v)) return;
                            if (numericOnly === "decimal" && v !== "" && !/^\d*\.?\d*$/.test(v)) return;
                            if (isApplyAll) handleApplyAllChange(field, v);
                            else handleRowChange(record.key, field, v);
                        }}
                    />
                    <span className="error_content">{error || ""}</span>
                </div>
            );
        },
    });

    // Sensitive Category modal column (shared)
    const makeBlockCategoryColumn = () => ({
        title: "Sensitive Category",
        dataIndex: "blockCategories",
        width: 175,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const categories = isApplyAll ? applyAllValues.blockCategories : value;
            const rowKey = isApplyAll ? "apply-all" : record.key;
            const count = categories instanceof Set ? categories.size : 0;
            const applyAllCats = applyAllValues.blockCategories;
            const applyAllHasValue = applyAllCats instanceof Set && applyAllCats.size > 0;
            const rowCats = categories instanceof Set ? categories : new Set();
            const setsMatch = applyAllHasValue && rowCats.size === applyAllCats.size && [...applyAllCats].every((c) => rowCats.has(c));
            const isOverridden = !isApplyAll && applyAllHasValue && !setsMatch;
            const dotCls = isOverridden ? " change_dot_cls" : "";
            if (isApplyAll) {
                return (
                    <span className="sensitive_tag sensitive_tag_purple"
                        onClick={() => openBlockCatModal(rowKey)}>
                        {count > 0 ? `${count} blocked` : "None"}
                    </span>
                );
            }
            if (count > 0) {
                return (
                    <span className={`sensitive_tag sensitive_tag_orange${dotCls}`}
                        onClick={() => openBlockCatModal(rowKey)}>
                        {count} blocked
                    </span>
                );
            }
            return (
                <span className={`sensitive_tag sensitive_tag_gray${dotCls}`}
                    onClick={() => openBlockCatModal(rowKey)}>
                    None
                </span>
            );
        },
    });

    // Pass Inventory Partner Domain + Inventory Partner Domain (shared)
    const makePartnerDomainColumn = () => ({
        title: "Partner Domain",
        dataIndex: "partnerDomain",
        width: 350,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues.partnerDomain : value;
            const checked = isApplyAll ? applyAllValues.passIPD : record.passIPD;
            // Yellow dot: show when Apply All has a value set AND this row differs
            const applyAllCheckboxSet = applyAllValues.passIPD === true;
            const isCheckboxDiff = !isApplyAll && applyAllCheckboxSet && record.passIPD !== applyAllValues.passIPD;
            const applyAllDomainSet = applyAllValues.partnerDomain !== "";
            const isDomainDiff = !isApplyAll && applyAllDomainSet && value !== applyAllValues.partnerDomain;
            return (
                <div className="form_content_block--group">

                    <Input
                        value={val}
                        placeholder="-"
                        className={`form_control basic${isDomainDiff ? " change_dot_cls" : ""}`}
                        onChange={(e) => {
                            if (isApplyAll) handleApplyAllChange("partnerDomain", e.target.value);
                            else handleRowChange(record.key, "partnerDomain", e.target.value);
                        }}
                    />
                    <div className={`primary_checkbox${isCheckboxDiff ? " change_dot_cls" : ""}`} style={{ marginTop: "0.6rem" }}>
                        <Checkbox
                            checked={checked}
                            style={{ fontSize: "1.2rem" }}
                            onChange={(e) => {
                                if (isApplyAll) handleApplyAllChange("passIPD", e.target.checked);
                                else handleRowChange(record.key, "passIPD", e.target.checked);
                            }}
                        >
                            Pass Inventory Partner Domain
                        </Checkbox>
                    </div>
                </div>
            );
        },
    });

    // ── Shared non-editable label columns (same for Prebid & Open RTB) ─────────

    const sharedTrafficTypeCol = () => makeLabelCol("Traffic Type", "trafficType", 130);

    const sharedFormatTypeCol = () => makeLabelCol("Format Type", "formatType", 170, (value) => {
        const arr = Array.isArray(value) ? value : (value ? [value] : []);
        return <span className="table_label_text">{arr.length > 0 ? arr.join(", ") : "—"}</span>;
    });

    // In Banner Video Allow – shows Yes/No only when Format Type includes "Banner", else "—"
    const sharedInBannerVideoCol = () => makeLabelCol("In Banner Video Allow", "bannerCheckbox", 160, (value, record) => {
        const hasBanner = Array.isArray(record.formatType)
            ? record.formatType.some((f) => f.toLowerCase() === "banner")
            : String(record.formatType || "").toLowerCase() === "banner";
        if (!hasBanner) return <span className="table_label_text">—</span>;
        if (value === null || value === undefined) return <span className="table_label_text">—</span>;
        return <span className="table_label_text">{value ? "Yes" : "No"}</span>;
    });

    // const sharedIsActiveCol = () => makeLabelCol("Is Zone Active", "isActive", 130, (value) => (
    //     <span
    //     // className={`table_label_badge ${value ? "badge_content--approved" : "badge_content--rejected"}`}
    //     >
    //         {value ? "Active" : "Inactive"}
    //     </span>
    // ));

    const sharedIntegrationTypeCol = () => makeLabelCol("Integration Type", "exchangeType", 160, (value) => {
        const match = exchangeTypeOptions.find((opt) => opt.value === value);
        return <span className="table_label_text">{match ? match.label : (value || "—")}</span>;
    });

    const sharedHighestBidFloorCol = () => ({
        title: "Use Highest Bid Floor",
        dataIndex: "isHighestBidFloor",
        width: 180,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const checked = isApplyAll ? applyAllValues.isHighestBidFloor : value;
            const minEcpmEmpty = !isApplyAll && (record.minEcpm === "" || record.minEcpm === null || record.minEcpm === undefined);
            const applyAllHasValue = applyAllValues.isHighestBidFloor !== null && applyAllValues.isHighestBidFloor !== undefined;
            const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.isHighestBidFloor;
            return (
                <div className={`primary_checkbox${isOverridden ? " change_dot_cls" : ""}`}>
                    <Checkbox
                        checked={minEcpmEmpty ? false : !!checked}
                        disabled={minEcpmEmpty}
                        onChange={(e) => {
                            if (isApplyAll) handleApplyAllChange("isHighestBidFloor", e.target.checked);
                            else handleRowChange(record.key, "isHighestBidFloor", e.target.checked);
                        }}
                    />
                </div>
            );
        },
    });

    const sharedReqDailyCol = () => makeLabelCol("Request Per Day", "reqDaily", 145, (value) => (
        <span className="table_label_text">
            {value !== "" && value !== undefined && value !== null
                ? Number(value).toLocaleString() : "—"}
        </span>
    ));

    const sharedImpDailyCol = () => makeLabelCol("Impressions Per Day", "impDaily", 165, (value) => (
        <span className="table_label_text">
            {value !== "" && value !== undefined && value !== null
                ? Number(value).toLocaleString() : "—"}
        </span>
    ));

    // ─── Prebid: column set ────────────────────────────────────────────────────
    const getPrebidColumns = () => [
        makeZoneNameColumn(),
        makeStatusColumn(),
        sharedTrafficTypeCol(),
        sharedFormatTypeCol(),
        sharedInBannerVideoCol(),
        // sharedIsActiveCol(),
        sharedIntegrationTypeCol(),
        makeInputCol("Rev Share", "revShare", 140, null, "%", "decimal"),
        makeBlockCategoryColumn(),
        makeInputCol("Min eCPM", "minEcpm", 130, "$", null, "decimal"),
        makeInputCol("Max eCPM", "maxEcpm", 130, "$", null, "decimal"),
        sharedHighestBidFloorCol(),
        sharedReqDailyCol(),
        sharedImpDailyCol(),
        makePartnerDomainColumn(),
        makeInputCol("Human", "human", 120, null, "%", "integer"),
        // PIXALATE SUNSET: Pixalate column removed
        // makeInputCol("Pixalate", "pixalate", 125, null, "%", "integer"),
        makeInputCol("DoubleVerify", "doubleverify", 130, null, "%", "integer"),
    ];

    // ─── Open RTB: column set (same structure as Prebid) ─────────────────────
    const getOpenRTBColumns = () => [
        makeZoneNameColumn(),
        makeStatusColumn(),
        sharedTrafficTypeCol(),
        sharedFormatTypeCol(),
        sharedInBannerVideoCol(),
        // sharedIsActiveCol(),
        sharedIntegrationTypeCol(),
        makeInputCol("Rev Share", "revShare", 140, null, "%", "decimal"),
        makeBlockCategoryColumn(),
        makeInputCol("Min eCPM", "minEcpm", 130, "$", null, "decimal"),
        makeInputCol("Max eCPM", "maxEcpm", 130, "$", null, "decimal"),
        sharedHighestBidFloorCol(),
        sharedReqDailyCol(),
        sharedImpDailyCol(),
        makePartnerDomainColumn(),
        makeInputCol("Human", "human", 120, null, "%", "integer"),
        // PIXALATE SUNSET: Pixalate column removed
        // makeInputCol("Pixalate", "pixalate", 125, null, "%", "integer"),
        makeInputCol("DoubleVerify", "doubleverify", 130, null, "%", "integer"),
    ];

    // ─── Vast Zone (ADK): specific columns ─────────────────────────────────────

    // Stream Position – non-editable label
    const vastStreamPositionCol = () => makeLabelCol("Stream Position", "streamPosition", 140);

    // eCPM (fixed_cpm) – non-editable label
    // eCPM + Override eCPM + Target eCPM merged into one column (Vast ADK)
    const vastEcpmCol = () => ({
        title: "eCPM",
        dataIndex: "fixedCpm",
        width: 280,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const checked = isApplyAll ? applyAllValues.isTargetEcpm : record.isTargetEcpm;
            const targetVal = isApplyAll ? applyAllValues.targetEcpm : record.targetEcpm;
            const applyAllCheckboxSet = applyAllValues.isTargetEcpm === true;
            const isCheckboxDiff = !isApplyAll && applyAllCheckboxSet && record.isTargetEcpm !== applyAllValues.isTargetEcpm;
            const applyAllTargetSet = applyAllValues.targetEcpm !== "" && applyAllValues.targetEcpm !== null;
            const isTargetDiff = !isApplyAll && applyAllTargetSet && record.targetEcpm !== applyAllValues.targetEcpm;
            const error = isApplyAll ? null : rowErrors[record.key]?.targetEcpm;
            return (
                <div className="form_content_block--group">
                    <div className='d-flex align-items-cneter gap_8'>
                        {!isApplyAll && (
                            <div className="form_label" style={{ marginBottom: "0.4rem", fontSize: "1.1rem", color: "#64748B" }}>
                                {value !== "" && value !== undefined && value !== null ? <Input value={value} disabled /> : "—"}
                            </div>
                        )}

                        {checked && (
                            <div>
                                <Input
                                    value={targetVal}
                                    placeholder="Enter eCPM"
                                    prefix="$"
                                    className={`form_control basic prefix_sign${isTargetDiff ? " change_dot_cls" : ""}${error ? " error" : ""}`}
                                    style={{ marginBottom: "0.6rem" }}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v !== "" && !/^\d*\.?\d*$/.test(v)) return;
                                        if (isApplyAll) {
                                            handleApplyAllChange("targetEcpm", v);
                                            const err = SHARED_FIELD_VALIDATORS.targetEcpm(v);
                                            setRowErrors((prev) => {
                                                const updated = { ...prev };
                                                rowsData.forEach((row) => {
                                                    updated[row.key] = { ...(updated[row.key] || {}), targetEcpm: err };
                                                });
                                                return updated;
                                            });
                                        } else {
                                            handleRowChange(record.key, "targetEcpm", v);
                                            const err = SHARED_FIELD_VALIDATORS.targetEcpm(v);
                                            setRowErrors((prev) => ({
                                                ...prev,
                                                [record.key]: { ...(prev[record.key] || {}), targetEcpm: err },
                                            }));
                                        }
                                    }}
                                />
                                {/* <span className="error_content">{error || ""}</span> */}
                            </div>
                        )}
                    </div>
                    <div className={`primary_checkbox${isCheckboxDiff ? " change_dot_cls" : ""}`}>
                        <Checkbox
                            checked={checked}
                            style={{ fontSize: "1.1rem" }}
                            onChange={(e) => {
                                if (isApplyAll) {
                                    handleApplyAllChange("isTargetEcpm", e.target.checked);
                                } else {
                                    handleRowChange(record.key, "isTargetEcpm", e.target.checked);
                                }
                            }}
                        >
                            <span className="form_label">Override the current eCPM</span>
                        </Checkbox>
                    </div>
                </div>
            );
        },
    });

    // Template – editable dropdown (options filtered by platform type per row)
    const vastTemplateCol = () => {
        // Check if all data rows share the same traffic type
        const trafficTypes = new Set(rowsData.map((r) => r.trafficType).filter(Boolean));
        const allSameTrafficType = trafficTypes.size === 1;
        const sharedTrafficType = allSameTrafficType ? [...trafficTypes][0] : null;

        return {
            title: "Template",
            dataIndex: "template",
            width: 220,
            render: (value, record) => {
                const isApplyAll = record.isApplyAll;
                if (isApplyAll && !allSameTrafficType) return null;
                const val = isApplyAll ? applyAllValues.template : value;
                const options = templateOptions[isApplyAll ? sharedTrafficType : record.trafficType] || [];
                const applyAllHasValue = applyAllValues.template !== null && applyAllValues.template !== undefined && applyAllValues.template !== "";
                const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.template;
                return (
                    <Select
                        className={`form_select basic${isOverridden ? " change_dot_cls" : ""}`}
                        style={{ width: "100%" }}
                        allowClear={false}
                        value={val || undefined}
                        placeholder="-"
                        showSearch
                        optionFilterProp="label"
                        suffixIcon={<ChevronDown />}
                        onChange={(v) => {
                            if (isApplyAll) handleApplyAllChange("template", v || "");
                            else handleRowChange(record.key, "template", v || "");
                        }}
                        options={options}
                    />
                );
            },
        };
    };

    // ─── Vast Zone: column set ───────────────────────────────────────────────
    const getVastColumns = () => [
        makeZoneNameColumn(),
        makeStatusColumn(),
        sharedTrafficTypeCol(),
        sharedFormatTypeCol(),
        vastStreamPositionCol(),
        // sharedIsActiveCol(),
        {
            title: "Revenue Type",
            width: 140,
            render: (_, record) => record.isApplyAll ? null : <span className="table_label_text">Fixed Price</span>,
        },
        vastEcpmCol(),
        makeBlockCategoryColumn(),
        sharedReqDailyCol(),
        sharedImpDailyCol(),
        makePartnerDomainColumn(),
        makeInputCol("Human", "human", 120, null, "%", "integer"),
        // PIXALATE SUNSET: Pixalate column removed
        // makeInputCol("Pixalate", "pixalate", 125, null, "%", "integer"),
        makeInputCol("DoubleVerify", "doubleverify", 130, null, "%", "integer"),
        vastTemplateCol(),
    ];

    // ─── Publica: specific columns ─────────────────────────────────────────────

    // Traffic Type – editable dropdown (Publica)
    // const publicaTrafficTypeCol = () => ({
    //     title: "Traffic Type",
    //     dataIndex: "trafficType",
    //     width: 150,
    //     render: (value, record) => {
    //         const isApplyAll = record.isApplyAll;
    //         const val = isApplyAll ? applyAllValues.trafficType : value;
    //         const applyAllHasValue = applyAllValues.trafficType !== null && applyAllValues.trafficType !== undefined;
    //         const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.trafficType;
    //         return (
    //             <Select
    //                 className={`form_select basic${isOverridden ? " change_dot_cls" : ""}`}
    //                 style={{ width: "100%" }}
    //                 value={val || undefined}
    //                 placeholder="-"
    //                 suffixIcon={<ChevronDown />}
    //                 onChange={(v) => {
    //                     if (isApplyAll) handleApplyAllChange("trafficType", v);
    //                     else handleRowChange(record.key, "trafficType", v);
    //                 }}
    //                 options={PUBLICA_TRAFFIC_TYPE_OPTIONS}
    //             />
    //         );
    //     },
    // });

    // Header Bidder Vast Version – editable dropdown
    const publicaVastVersionCol = () => ({
        title: "Header Bidder Vast Version",
        dataIndex: "hbVastVersion",
        width: 200,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues.hbVastVersion : value;
            const applyAllHasValue = applyAllValues.hbVastVersion !== null && applyAllValues.hbVastVersion !== undefined;
            const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.hbVastVersion;
            return (
                <Select
                    className={`form_select basic${isOverridden ? " change_dot_cls" : ""}`}
                    style={{ width: "100%" }}
                    value={val || undefined}
                    placeholder="-"
                    suffixIcon={<ChevronDown />}
                    onChange={(v) => {
                        if (isApplyAll) handleApplyAllChange("hbVastVersion", v);
                        else handleRowChange(record.key, "hbVastVersion", v);
                    }}
                    options={PUBLICA_VAST_VERSION_OPTIONS}
                />
            );
        },
    });

    // Channel Name – editable input with validation
    // const publicaChannelNameCol = () => ({
    //     title: "Channel Name",
    //     dataIndex: "channelName",
    //     width: 180,
    //     render: (value, record) => {
    //         const isApplyAll = record.isApplyAll;
    //         const val = isApplyAll ? applyAllValues.channelName : value;
    //         const applyAllHasValue = applyAllValues.channelName !== "" && applyAllValues.channelName !== null;
    //         const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.channelName;
    //         const error = isApplyAll ? null : rowErrors[record.key]?.channelName;
    //         return (
    //             <div className="form_content_block--group">
    //                 <Input
    //                     value={val}
    //                     className={`form_control basic${isOverridden ? " change_dot_cls" : ""}${error ? " error" : ""}`}
    //                     placeholder="-"
    //                     onChange={(e) => {
    //                         if (isApplyAll) handleApplyAllChange("channelName", e.target.value);
    //                         else handleRowChange(record.key, "channelName", e.target.value);
    //                     }}
    //                 />
    //                 <span className="error_content">{error || ""}</span>
    //             </div>
    //         );
    //     },
    // });

    // Region – editable dropdown with validation
    const publicaRegionCol = () => ({
        title: "Region",
        dataIndex: "region",
        width: 150,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues.region : value;
            const applyAllHasValue = applyAllValues.region !== null && applyAllValues.region !== undefined;
            const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.region;
            const error = isApplyAll ? null : rowErrors[record.key]?.region;
            return (
                // <div className="form_content_block--group">
                <>
                    <Select
                        className={`form_select basic${isOverridden ? " change_dot_cls" : ""}${error ? " error" : ""}`}
                        style={{ width: "100%" }}
                        showSearch={false}
                        value={val || undefined}
                        placeholder="-"
                        suffixIcon={<ChevronDown />}
                        onChange={(v) => {
                            if (isApplyAll) handleApplyAllChange("region", v);
                            else handleRowChange(record.key, "region", v);
                        }}
                        options={PUBLICA_REGION_OPTIONS}
                    />
                    <span className="error_content">{error || ""}</span>
                </>
                // </div>
            );
        },
    });

    // eCPM – non-editable label (Publica uses eCpm field)
    // eCPM + Override eCPM + Target eCPM merged into one column (Publica)
    const publicaEcpmCol = () => ({
        title: "eCPM",
        dataIndex: "eCpm",
        width: 280,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const checked = isApplyAll ? applyAllValues.isTargetEcpm : record.isTargetEcpm;
            const targetVal = isApplyAll ? applyAllValues.targetEcpm : record.targetEcpm;
            const applyAllCheckboxSet = applyAllValues.isTargetEcpm === true;
            const isCheckboxDiff = !isApplyAll && applyAllCheckboxSet && record.isTargetEcpm !== applyAllValues.isTargetEcpm;
            const applyAllTargetSet = applyAllValues.targetEcpm !== "" && applyAllValues.targetEcpm !== null;
            const isTargetDiff = !isApplyAll && applyAllTargetSet && record.targetEcpm !== applyAllValues.targetEcpm;
            const error = isApplyAll ? null : rowErrors[record.key]?.targetEcpm;
            return (
                <div className="form_content_block--group">
                    <div className='d-flex align-items-center gap_8'>
                        {!isApplyAll && (
                            <div className="form_label" style={{ marginBottom: "0.4rem", width: '100%', fontSize: "1.1rem", color: "#64748B" }}>
                                {value !== "" && value !== undefined && value !== null ? <Input value={value} disabled /> : "—"}
                            </div>
                        )}

                        {checked && (
                            <div>
                                <Input
                                    value={targetVal}
                                    placeholder="Enter eCPM"
                                    prefix="$"
                                    // className={`form_control basic prefix_sign${isTargetDiff ? " change_dot_cls" : ""}${error ? " error" : ""}`}
                                    className={`form_control basic prefix_sign${isTargetDiff ? "" : ""}${error ? " error" : ""}`}
                                    style={{ marginBottom: "0.4rem", width: '100%' }}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (!/^\d*\.?\d{0,2}$/.test(v)) return;
                                        if (isApplyAll) {
                                            handleApplyAllChange("targetEcpm", v);
                                            const err = SHARED_FIELD_VALIDATORS.targetEcpm(v);
                                            setRowErrors((prev) => {
                                                const updated = { ...prev };
                                                rowsData.forEach((row) => {
                                                    updated[row.key] = { ...(updated[row.key] || {}), targetEcpm: err };
                                                });
                                                return updated;
                                            });
                                        } else {
                                            handleRowChange(record.key, "targetEcpm", v);
                                            const err = SHARED_FIELD_VALIDATORS.targetEcpm(v);
                                            setRowErrors((prev) => ({
                                                ...prev,
                                                [record.key]: { ...(prev[record.key] || {}), targetEcpm: err },
                                            }));
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const v = e.target.value.trim();
                                        if (v.length > 0 && Number(v) >= 0) {
                                            const newBid = calcBidAdjustment(v);
                                            if (isApplyAll) {
                                                handleApplyAllChange("bidAdjustment", newBid);
                                            } else {
                                                handleRowChange(record.key, "bidAdjustment", newBid);
                                            }
                                        }
                                    }}
                                />
                                {/* <span className="error_content">{error || ""}</span> */}
                            </div>
                        )}
                    </div>
                    <div className={`primary_checkbox${isCheckboxDiff ? " change_dot_cls" : ""}`}>
                        <Checkbox
                            checked={checked}
                            style={{ fontSize: "1.1rem" }}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                if (isApplyAll) {
                                    // Update isTargetEcpm + bidAdjustment in a single batch
                                    setApplyAllValues((prev) => ({ ...prev, isTargetEcpm: isChecked, bidAdjustment: "" }));
                                    setRowsData((prev) =>
                                        prev.map((row) => {
                                            const overrides = new Set(row.manualOverrideFields);
                                            overrides.delete("isTargetEcpm");
                                            overrides.delete("bidAdjustment");
                                            const updates = { isTargetEcpm: isChecked, manualOverrideFields: overrides };
                                            if (!isChecked) {
                                                // Unchecked: recalc bidAdjustment from each row's own eCpm
                                                updates.bidAdjustment = row.eCpm ? calcBidAdjustment(row.eCpm) : row.bidAdjustment;
                                            } else {
                                                // Checked: recalc bidAdjustment from applyAll targetEcpm if available
                                                const tgt = applyAllValues.targetEcpm;
                                                if (tgt !== "" && tgt !== null && tgt !== undefined) {
                                                    updates.bidAdjustment = calcBidAdjustment(tgt);
                                                }
                                            }
                                            return { ...row, ...updates };
                                        })
                                    );
                                } else {
                                    // Update isTargetEcpm + bidAdjustment together in one setRowsData call
                                    // to avoid React batching overwriting one change with the other
                                    setRowsData((prev) =>
                                        prev.map((row) => {
                                            if (row.key !== record.key) return row;
                                            const overrides = new Set(row.manualOverrideFields);
                                            overrides.add("isTargetEcpm");
                                            const updates = { isTargetEcpm: isChecked, manualOverrideFields: overrides };
                                            if (!isChecked) {
                                                // Unchecked: recalc bidAdjustment from row's eCpm
                                                if (row.eCpm) {
                                                    overrides.add("bidAdjustment");
                                                    updates.bidAdjustment = calcBidAdjustment(row.eCpm);
                                                }
                                            } else {
                                                // Checked: recalc bidAdjustment from row's targetEcpm if present
                                                if (row.targetEcpm !== "" && row.targetEcpm !== null && row.targetEcpm !== undefined) {
                                                    overrides.add("bidAdjustment");
                                                    updates.bidAdjustment = calcBidAdjustment(row.targetEcpm);
                                                }
                                            }
                                            return { ...row, ...updates };
                                        })
                                    );
                                }
                            }}
                        >
                            <span className="form_label">Override the current eCPM</span>
                        </Checkbox>
                    </div>
                </div>
            );
        },
    });

    // Bid Adjustment – editable input with % suffix and validation
    const publicaBidAdjustmentCol = () => ({
        title: "Bid Adjustment",
        dataIndex: "bidAdjustment",
        width: 160,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues.bidAdjustment : value;
            const applyAllHasValue = applyAllValues.bidAdjustment !== "" && applyAllValues.bidAdjustment !== null;
            const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.bidAdjustment;
            const error = isApplyAll ? null : rowErrors[record.key]?.bidAdjustment;
            return (
                <div className="form_content_block--group">
                    <Input
                        value={val}
                        suffix="%"
                        className={`form_control basic subfix_sign${isOverridden ? " change_dot_cls" : ""}${error ? " error" : ""}`}
                        placeholder="0"
                        onChange={(e) => {
                            if (!/^\d*\.?\d{0,2}$/.test(e.target.value)) return;
                            if (isApplyAll) handleApplyAllChange("bidAdjustment", e.target.value);
                            else handleRowChange(record.key, "bidAdjustment", e.target.value);
                        }}
                    />
                    <span className="error_content">{error || ""}</span>
                </div>
            );
        },
    });

    // Bid Adjustment Value – editable input with $ prefix
    const publicaBidAdjustmentValueCol = () => ({
        title: "Bid Adjustment Value",
        dataIndex: "bidAdjustmentValue",
        width: 180,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues.bidAdjustmentValue : value;
            const applyAllHasValue = applyAllValues.bidAdjustmentValue !== "" && applyAllValues.bidAdjustmentValue !== null;
            const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.bidAdjustmentValue;
            const error = isApplyAll ? null : rowErrors[record.key]?.bidAdjustmentValue;
            return (
                <div className="form_content_block--group">
                    <Input
                        value={val}
                        prefix="$"
                        className={`form_control basic prefix_sign${isOverridden ? " change_dot_cls" : ""}${error ? " error" : ""}`}
                        placeholder="0"
                        onChange={(e) => {
                            if (!/^\d*\.?\d{0,2}$/.test(e.target.value)) return;
                            if (isApplyAll) handleApplyAllChange("bidAdjustmentValue", e.target.value);
                            else handleRowChange(record.key, "bidAdjustmentValue", e.target.value);
                        }}
                    />
                    <span className="error_content">{error || ""}</span>
                </div>
            );
        },
    });

    // Publica Pass IPD column (different labels: checkbox = "Add Value only if obj, empty in bid request", input = "Override")
    const publicaPartnerDomainColumn = () => ({
        title: "Pass IPD & Partner Domain",
        dataIndex: "partnerDomain",
        width: 350,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues.partnerDomain : value;
            const checked = isApplyAll ? applyAllValues.passIPD : record.passIPD;
            const applyAllCheckboxSet = applyAllValues.passIPD === true;
            const isCheckboxDiff = !isApplyAll && applyAllCheckboxSet && record.passIPD !== applyAllValues.passIPD;
            const applyAllDomainSet = applyAllValues.partnerDomain !== "";
            const isDomainDiff = !isApplyAll && applyAllDomainSet && value !== applyAllValues.partnerDomain;
            return (
                <div className="form_content_block--group">
                    <div >
                        <div className="form_label" style={{ marginBottom: "0.3rem", fontSize: "1.1rem", color: "#64748B" }}>Override</div>
                        <Input
                            value={val}
                            placeholder="-"
                            className={`form_control basic${isDomainDiff ? " change_dot_cls" : ""}`}
                            onChange={(e) => {
                                if (isApplyAll) handleApplyAllChange("partnerDomain", e.target.value);
                                else handleRowChange(record.key, "partnerDomain", e.target.value);
                            }}
                        />
                    </div>
                    <div className={`primary_checkbox${isCheckboxDiff ? " change_dot_cls" : ""}`}>
                        <Checkbox
                            checked={checked}
                            style={{ fontSize: "1.2rem", marginTop: "0.6rem" }}
                            onChange={(e) => {
                                if (isApplyAll) handleApplyAllChange("passIPD", e.target.checked);
                                else handleRowChange(record.key, "passIPD", e.target.checked);
                            }}
                        >
                            Add Value only if obj, empty in bid request
                        </Checkbox>
                    </div>

                </div>
            );
        },
    });

    // Template – editable dropdown (Publica, same pattern as Vast — no Apply All, filtered by trafficType)
    // Template – Publica uses flat options from publica/templates (not keyed by platform)
    const publicaTemplateCol = () => ({
        title: "Template",
        dataIndex: "template",
        width: 220,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const val = isApplyAll ? applyAllValues.template : value;
            const applyAllHasValue = applyAllValues.template !== null && applyAllValues.template !== undefined && applyAllValues.template !== "";
            const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.template;
            return (
                <Select
                    className={`form_select basic${isOverridden ? " change_dot_cls" : ""}`}
                    style={{ width: "100%" }}
                    value={val || undefined}
                    placeholder="-"
                    showSearch
                    optionFilterProp="label"
                    suffixIcon={<ChevronDown />}
                    allowClear={false}
                    onChange={(v) => {
                        if (isApplyAll) handleApplyAllChange("template", v || "");
                        else handleRowChange(record.key, "template", v || "");
                    }}
                    options={publicaTemplateOptions}
                />
            );
        },
    });

    // Publica: Channel Name as first column (replaces Zone Name), editable input
    const publicaChannelNameFixedCol = () => ({
        title: "Channel Name",
        dataIndex: "channelName",
        width: 350,
        fixed: "left",
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            if (isApplyAll) {
                return (
                    <div className="apply_all_row">
                        <span className="dot"></span>
                        <span className="apply_text">Apply to All</span>
                        <span className="apply_desc">fills all zones</span>
                    </div>
                );
            }
            const applyAllHasValue = applyAllValues.channelName !== "" && applyAllValues.channelName !== null;
            const isOverridden = applyAllHasValue && value !== applyAllValues.channelName;
            const error = rowErrors[record.key]?.channelName;
            // flag when the name already exists (per-row uniqueness check)
            const nameExists = gamZoneNameExists[record.key];
            const nameChecking = gamZoneNameChecking[record.key];
            return (
                <div className="form_content_block--group">
                    <Input
                        value={value}
                        className={`form_control basic${isOverridden ? " change_dot_cls" : ""}${error || nameExists ? " error" : ""}`}
                        placeholder="-"
                        suffix={nameChecking ? <img src={loaderIcon} alt="" style={{ width: 16, height: 16 }} /> : <span />}
                        onChange={(e) => {
                            handleRowChange(record.key, "channelName", e.target.value);
                            setGamZoneNameExists((p) => ({ ...p, [record.key]: false }));
                            debouncedCheckGamZoneName(record.key, e.target.value);
                        }}
                        onBlur={(e) => {
                            if (gamZoneNameCheckTimers.current[record.key]) clearTimeout(gamZoneNameCheckTimers.current[record.key]);
                            checkGamZoneNameUnique(record.key, e.target.value);
                        }}
                    />
                    <div className="zone_id mt-2">{record.zoneId}</div>
                    {nameExists && <span className="error_content">Zone name already exists</span>}
                </div>
            );
        },
    });

    // ─── Publica: column set ──────────────────────────────────────────────────
    const getPublicaColumns = () => [
        publicaChannelNameFixedCol(),
        makeStatusColumn(),
        makeLabelCol("Traffic Type", "trafficType", 130),
        sharedFormatTypeCol(),
        publicaVastVersionCol(),
        publicaRegionCol(),
        publicaEcpmCol(),
        publicaBidAdjustmentCol(),
        publicaBidAdjustmentValueCol(),
        makeInputCol("ASI", "asi", 140),
        makeInputCol("SID", "sid", 140),
        makeInputCol("Name", "scName", 160),
        makeInputCol("Domain", "scDomain", 160),
        publicaPartnerDomainColumn(),
        publicaTemplateCol(),
    ];

    // ─── GAM: specific columns ─────────────────────────────────────────────────

    // Reusable searchable multi-select (sizes / companion) with Select-All
    const renderGamMultiSelect = ({ dropdownKey, options, selected, onToggle, onSelectAll, onChangeAll, error }) => {
        const sel = selected || [];
        const filtered = (options || []).filter((o) => o.label.toLowerCase().includes(gamSearch.toLowerCase()));
        const allSelected = options?.length > 0 && sel.length === options.length;
        const indeterminate = sel.length > 0 && sel.length !== options?.length;
        return (
            <div className="checkbox_value_select_d2 multi_select_tag_d2 w-100">
                <Select
                    value={sel}
                    popupClassName="diff_select_d2 form_select_multiple--dropdown"
                    maxTagCount={1}
                    showSearch={false}
                    placeholder="- Select -"
                    maxTagPlaceholder={(omitted) => `+${omitted.length}`}
                    className={error ? "form_select_multiple error" : "form_select_multiple"}
                    mode="multiple"
                    optionLabelProp="label"
                    style={{ width: "100%" }}
                    onChange={onChangeAll}
                    open={!!openSelectState_gam[dropdownKey]}
                    onDropdownVisibleChange={(open) => toggleSelectOpen_gam(dropdownKey, open)}
                    dropdownRender={(menu) => (
                        <>
                            <div className="custom_inside_searchbar_d2">
                                <Input
                                    placeholder="Search"
                                    value={gamSearch}
                                    onChange={(e) => setGamSearch(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    ref={getRef_gam(dropdownKey)}
                                    prefix={<img src={searchIcon} alt="" />}
                                    allowClear
                                    onClear={() => {
                                        setGamSearch("");
                                        setTimeout(() => { refsMap_gam.current[dropdownKey]?.current?.focus(); }, 0);
                                    }}
                                />
                            </div>
                            {gamSearch.length === 0 && options?.length > 0 &&
                                <div
                                    className={`select_all_d2 ${indeterminate ? "intermediate_state" : ""}`}
                                    onClick={onSelectAll}
                                >
                                    <Checkbox checked={allSelected} onClick={(e) => e.stopPropagation()}>
                                        Select All
                                    </Checkbox>
                                </div>
                            }
                            {menu}
                        </>
                    )}
                >
                    {[
                        ...filtered.filter((o) => sel.includes(o.value)),
                        ...filtered.filter((o) => !sel.includes(o.value)),
                    ].map((o) => (
                        <Option key={o.value} value={o.value} label={o.label}>
                            <div onClick={() => onToggle(o.value)} style={{ display: "flex", alignItems: "center" }}>
                                <Checkbox checked={sel.includes(o.value)} onClick={(e) => e.stopPropagation()}>
                                    {o.label}
                                </Checkbox>
                            </div>
                        </Option>
                    ))}
                </Select>
            </div>
        );
    };

    const gamTrafficTypeCol = () => makeLabelCol("Traffic Type", "trafficType", 130);

    const gamFormatTypeCol = () => makeLabelCol("Format Type", "formatType", 130, (value, record) => (
        <span className="table_label_text">{record.trafficType === "CTV" ? "Video" : (value || "—")}</span>
    ));

    const gamActiveCol = () => makeLabelCol("Is Zone Active", "isActive", 140, (value) => (
        <span className="table_label_text">{value ? "Active" : "Inactive"}</span>
    ));

    const gamFixedPriceCol = () => makeLabelCol("Fixed Price", "fixedPrice", 130, (value) => (
        <span className="table_label_text">{value !== "" && value !== null && value !== undefined ? `$${value}` : "—"}</span>
    ));

    const gamSetUprCol = () => makeLabelCol("Set UPR", "upr", 130, (value) => (
        <span className="table_label_text">{value !== "" && value !== null && value !== undefined ? `$${value}` : "—"}</span>
    ));

    // Sizes – editable multi-select. Apply-All only when all rows share the same format.
    const gamSizesCol = (allSameFormat, sharedFormat) => ({
        title: "Sizes",
        dataIndex: "sizes",
        width: 320,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            if (isApplyAll) return null; // Sizes not available in Apply-to-All
            const fmt = isApplyAll ? sharedFormat : record.formatType;
            const opts = fmt === "Video" ? gamVastMasterSizesData : gamSizesData;
            const rowKey = isApplyAll ? "apply-all" : record.key;
            const sel = (isApplyAll ? applyAllValues.sizes : value) || [];
            const applyAllArr = applyAllValues.sizes;
            const applyAllHasValue = Array.isArray(applyAllArr) && applyAllArr.length > 0;
            const matches = applyAllHasValue && sel.length === applyAllArr.length && applyAllArr.every((v) => sel.includes(v));
            const isOverridden = !isApplyAll && applyAllHasValue && !matches;
            const error = isApplyAll ? null : rowErrors[record.key]?.sizes;
            const setVal = (arr) => isApplyAll ? handleApplyAllChange("sizes", arr) : handleRowChange(record.key, "sizes", arr);
            const toggle = (v) => setVal(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
            const selectAll = () => setVal(sel.length === opts.length ? [] : opts.map((o) => o.value));
            return (
                <div className={`form_content_block--group${isOverridden ? " change_dot_cls" : ""}`}>
                    {renderGamMultiSelect({ dropdownKey: `sizes-${rowKey}`, options: opts, selected: sel, onToggle: toggle, onSelectAll: selectAll, onChangeAll: setVal, error })}
                    <span className="error_content">{error || ""}</span>
                </div>
            );
        },
    });

    // Companion – editable multi-select, Video rows only. Apply-All only when all rows are Video.
    const gamCompanionCol = (allVideo) => ({
        title: "Companion",
        dataIndex: "companion",
        width: 320,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            if (isApplyAll) return null; // Companion not available in Apply-to-All
            if (record.formatType !== "Video") return <span className="table_label_text">—</span>;
            const rowKey = isApplyAll ? "apply-all" : record.key;
            const sel = (isApplyAll ? applyAllValues.companion : value) || [];
            const applyAllArr = applyAllValues.companion;
            const applyAllHasValue = Array.isArray(applyAllArr) && applyAllArr.length > 0;
            const matches = applyAllHasValue && sel.length === applyAllArr.length && applyAllArr.every((v) => sel.includes(v));
            const isOverridden = !isApplyAll && applyAllHasValue && !matches;
            const opts = gamCompanionSizesData;
            const setVal = (arr) => isApplyAll ? handleApplyAllChange("companion", arr) : handleRowChange(record.key, "companion", arr);
            const toggle = (v) => setVal(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
            const selectAll = () => setVal(sel.length === opts.length ? [] : opts.map((o) => o.value));
            return (
                <div className={`form_content_block--group${isOverridden ? " change_dot_cls" : ""}`}>
                    {renderGamMultiSelect({ dropdownKey: `companion-${rowKey}`, options: opts, selected: sel, onToggle: toggle, onSelectAll: selectAll, onChangeAll: setVal })}
                </div>
            );
        },
    });

    // TV Application – checkbox + select, CTV rows only. Apply-All only when all rows are CTV.
    const gamTvAppCol = (allCTV) => ({
        title: "TV Application",
        dataIndex: "tvApplication",
        width: 280,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            if (isApplyAll) { if (!allCTV) return null; }
            else if (record.trafficType !== "CTV") return <span className="table_label_text">—</span>;
            const checked = isApplyAll ? applyAllValues.tvAppCheckbox : record.tvAppCheckbox;
            const val = isApplyAll ? applyAllValues.tvApplication : value;
            const error = isApplyAll ? null : rowErrors[record.key]?.tvApplication;
            const applyAllCheckboxSet = applyAllValues.tvAppCheckbox === true;
            const isCheckboxDiff = !isApplyAll && applyAllCheckboxSet && record.tvAppCheckbox !== applyAllValues.tvAppCheckbox;
            const setCheckbox = (c) => {
                if (isApplyAll) {
                    setApplyAllValues((prev) => ({ ...prev, tvAppCheckbox: c, tvApplication: c ? prev.tvApplication : "" }));
                    gamApplyAllRows(["tvAppCheckbox", "tvApplication"], (r) => r.trafficType === "CTV" ? { tvAppCheckbox: c, tvApplication: c ? r.tvApplication : "" } : {});
                } else {
                    gamSetRowFields(record.key, { tvAppCheckbox: c, tvApplication: c ? record.tvApplication : "" }, ["tvAppCheckbox", "tvApplication"]);
                    if (!c) clearRowError_gam(record.key, "tvApplication");
                }
            };
            const setApp = (v) => {
                if (isApplyAll) {
                    setApplyAllValues((prev) => ({ ...prev, tvApplication: v }));
                    gamApplyAllRows(["tvApplication"], (r) => r.trafficType === "CTV" ? { tvApplication: v } : {});
                } else {
                    gamSetRowFields(record.key, { tvApplication: v }, ["tvApplication"]);
                    clearRowError_gam(record.key, "tvApplication");
                }
            };
            return (
                <div className="form_content_block--group">
                    <div className={`primary_checkbox${isCheckboxDiff ? " change_dot_cls" : ""}`} style={{ marginBottom: checked ? "0.6rem" : 0 }}>
                        <Checkbox checked={checked} onChange={(e) => setCheckbox(e.target.checked)}>
                            <span className="form_label">TV app name</span>
                        </Checkbox>
                    </div>
                    {checked && (
                        <>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                className={`form_select basic${error ? " error" : ""}`}
                                style={{ width: "100%" }}
                                value={val}
                                placeholder="- Select -"
                                // suffixIcon={<ChevronDown />}
                                onChange={setApp}
                                options={tvApplicationOptions_gam}
                            />
                            <span className="error_content">{error || ""}</span>
                        </>
                    )}
                </div>
            );
        },
    });

    // Override UPR checkbox + conditional Override Set UPR input
    const gamOverrideUprCol = () => ({
        title: "Override UPR",
        dataIndex: "overrideSetUpr",
        width: 260,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            const checked = isApplyAll ? applyAllValues.overrideUpr : record.overrideUpr;
            const val = isApplyAll ? applyAllValues.overrideSetUpr : value;
            const applyAllCheckboxSet = applyAllValues.overrideUpr === true;
            const isCheckboxDiff = !isApplyAll && applyAllCheckboxSet && record.overrideUpr !== applyAllValues.overrideUpr;
            const applyAllValSet = applyAllValues.overrideSetUpr !== "" && applyAllValues.overrideSetUpr !== null;
            const isValDiff = !isApplyAll && applyAllValSet && value !== applyAllValues.overrideSetUpr;
            const error = isApplyAll ? null : rowErrors[record.key]?.overrideSetUpr;
            return (
                <div className="form_content_block--group">
                    <div className={`primary_checkbox${isCheckboxDiff ? " change_dot_cls" : ""}`} style={{ marginBottom: checked ? "0.6rem" : 0 }}>
                        <Checkbox
                            checked={checked}
                            onChange={(e) => isApplyAll ? handleGamApplyAllOverrideUprToggle(e.target.checked) : handleGamOverrideUprToggle(record.key, e.target.checked)}
                        >
                            <span className="form_label">Override UPR</span>
                        </Checkbox>
                    </div>
                    {checked && (
                        <>
                            <Input
                                value={val}
                                prefix="$"
                                placeholder="0"
                                className={`form_control basic prefix_sign${isValDiff ? " change_dot_cls" : ""}${error ? " error" : ""}`}
                                onChange={(e) => isApplyAll ? handleGamApplyAllOverrideSetUprChange(e.target.value) : handleGamOverrideSetUprChange(record.key, e.target.value)}
                                onBlur={(e) => isApplyAll ? handleGamApplyAllOverrideSetUprBlur(e.target.value) : handleGamOverrideSetUprBlur(record.key, e.target.value)}
                            />
                            <span className="error_content">{error || ""}</span>
                        </>
                    )}
                </div>
            );
        },
    });

    // UPR Category – editable select with auto-match feedback
    const gamUprCategoryCol = () => ({
        title: "UPR Category",
        dataIndex: "uprCategory",
        width: 220,
        render: (value, record) => {
            const isApplyAll = record.isApplyAll;
            if (isApplyAll) return null; // UPR Category not available in Apply-to-All
            const val = isApplyAll ? applyAllValues.uprCategory : value;
            const checked = isApplyAll ? applyAllValues.overrideUpr : record.overrideUpr;
            const applyAllHasValue = applyAllValues.uprCategory !== null && applyAllValues.uprCategory !== undefined && applyAllValues.uprCategory !== "";
            const isOverridden = !isApplyAll && applyAllHasValue && value !== applyAllValues.uprCategory;
            const requiredErr = !isApplyAll ? rowErrors[record.key]?.uprCategory : null;
            const error = isApplyAll ? null : (requiredErr || (record.uprCategoryNotFound ? "UPR Category as per UPR not found" : null));
            const mismatch = !isApplyAll && checked && !record.uprCategoryNotFound && !requiredErr && isUprCategoryMismatch_gam(record);
            const matched = !isApplyAll && record.uprCategoryMatched && !record.uprCategoryNotFound && record.uprCategory && !mismatch;
            return (
                <div className="form_content_block--group">
                    <Select
                        className={`form_select basic${isOverridden ? " change_dot_cls" : ""}${error ? " error" : ""}`}
                        style={{ width: "100%" }}
                        value={val}
                        placeholder="- Select -"
                        showSearch
                        optionFilterProp="label"
                        // suffixIcon={<ChevronDown />}
                        disabled={!checked}
                        onChange={(v) => isApplyAll ? handleGamApplyAllUprCategoryChange(v) : handleGamUprCategoryChange(record.key, v)}
                        options={uprCategoryOptions_gam}
                    />
                    <span className="error_content">{error || ""}</span>
                    {matched && (
                        <span className="error_content" style={{ color: "#12B76A" }}>UPR Category set as per UPR</span>
                    )}
                    {mismatch && (
                        <div className="upr_category_info">
                            <div className="d-flex align-items-center justify-content-between">
                                <p>UPR Category does not match the price. Please cross-check the details or proceed if you wish to continue.</p>
                            </div>
                        </div>
                    )}
                </div>
            );
        },
    });

    // ─── GAM: column set ──────────────────────────────────────────────────────
    const getGamColumns = () => {
        const formatTypes = new Set(rowsData.map((r) => r.formatType).filter(Boolean));
        const allSameFormat = formatTypes.size === 1;
        const sharedFormat = allSameFormat ? [...formatTypes][0] : null;
        const allVideo = rowsData.length > 0 && rowsData.every((r) => r.formatType === "Video");
        const allCTV = rowsData.length > 0 && rowsData.every((r) => r.trafficType === "CTV");
        return [
            makeZoneNameColumn(),
            makeStatusColumn(),
            gamTrafficTypeCol(),
            gamFormatTypeCol(),
            // gamActiveCol(),
            gamSizesCol(allSameFormat, sharedFormat),
            gamCompanionCol(allVideo),
            gamTvAppCol(allCTV),
            gamFixedPriceCol(),
            gamSetUprCol(),
            gamOverrideUprCol(),
            gamUprCategoryCol(),
        ];
    };

    const getColumns = () => {
        if (zoneType === "Prebid") return getPrebidColumns();
        if (zoneType === "Open RTB") return getOpenRTBColumns();
        if (zoneType === "Vast Zone") return getVastColumns();
        if (zoneType === "publica") return getPublicaColumns();
        if (zoneType === "GAM") return getGamColumns();
        return [makeZoneNameColumn()];
    };

    // ── Derived ────────────────────────────────────────────────────────────────
    const tableData = [{ key: "apply-all", isApplyAll: true }, ...rowsData];
    const totalValues = countValuesSet();
    const zoneCount = rowsData.length;

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="container_box--wrapper pt_header">



                {/* Breadcrumb */}
                <div className="container_box--breadcrumb">
                    <ul className="container_box--breadcrumb-list">
                        <li>
                            <Link onClick={() => handlePageViewChange("table", "no")}>
                                <img src={zoneIcon} alt="Zones" /> Zones
                            </Link>
                        </li>
                        <li className="primary">Edit Selected Zones</li>
                    </ul>
                </div>

                {/* Page title */}
                <div className="container_box--wrapper--title d-flex align-items-center justify-content-between">
                    <div className="back_btn_d2">
                        <img
                            src={backButton}
                            className="img-fluid"
                            alt=""
                            onClick={() => handlePageViewChange("table", "no")}
                            style={{ cursor: "pointer" }}
                        />
                        <div className="d-flex align-items-center" style={{ gap: "0.8rem" }}>
                            <h1 className="heading">Edit Selected Zones</h1>
                            {formVisible &&
                                <>
                                    <small className="vertical_divider_line"></small>
                                    <span className="bg_blue_badge">
                                        {zoneCount} Zone{zoneCount !== 1 ? "s" : ""} Selected
                                    </span>
                                </>
                            }
                        </div>
                    </div>
                    <div className="apply_to_all_text">
                        <InfoIcon />
                        Use the <span>Apply to All</span> row to fill all zones — or edit cells individually per zone.
                    </div>
                </div>
                {formVisible &&
                    <>
                        {/* Zone Type (fixed, non-editable, shown at top) */}
                        <div className="fixed_value_div">
                            <p>Zone Type: <span>{ZONE_TYPE_DISPLAY[zoneType] || zoneType}</span></p>
                        </div>

                        {/* IVT sampling alert (not applicable to GAM) */}
                        {zoneType !== "GAM" &&
                            <div className="yellow_alert_box">
                                <p>
                                    <span>IVT Sampling note:</span> Changing Human % or Pixalate % impacts your effective bid price. Review floor prices after saving.
                                </p>
                            </div>
                        }

                        {/* Table */}
                        <div className={`table_and_filter_div --without_filter_div${!dataLoading && tableData.length === 0 ? " --no_data_div_height" : ""}`}>
                            <div className="table_area_div">
                                <div className="table_content--box --new_layout_table_design table_empty_screen_d2 user_sync_table_v2">
                                    <div className="zones_table_wrapper">
                                        {dataLoading ? (
                                            <div style={{ padding: "4rem", textAlign: "center", color: "#64748B" }}>
                                                Loading zone details…
                                            </div>
                                        ) : (
                                            <Table
                                                columns={getColumns()}
                                                dataSource={tableData}
                                                pagination={false}
                                                bordered
                                                scroll={{ x: "max-content", y: "calc(100vh - 46.8rem)" }}
                                                rowClassName={(record) => (record.isApplyAll ? "apply_all_table_row" : "")}
                                            />
                                        )}
                                        <div className="table_tags_text">
                                            <p>
                                                <small style={YELLOW_DOT_STYLE}></small>
                                                Cell overrides Apply-All
                                            </p>
                                            <p>
                                                <BlockCatIcon />
                                                Click to pick blocked categories per zone
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div>
                            <div className="hr_custom" style={{ margin: "1.2rem 0 1.6rem", backgroundColor: "#CBD5E1" }}></div>
                            <div className="btn_group justify-content-end align-items-center gap_8">
                                <p className="m-0 btn_text_left">
                                    <strong>{totalValues}</strong> values set across <strong>{zoneCount}</strong> zone{zoneCount !== 1 ? "s" : ""}
                                </p>
                                <button
                                    type="button"
                                    className="w_120_d2 btn btn_white_outline w_fit_content"
                                    onClick={() => handlePageViewChange("table", "no")}
                                    style={{
                                        pointerEvents: buttonProcessing ? "none" : "auto",
                                        opacity: buttonProcessing ? "0.5" : "1",
                                    }}
                                >
                                    Discard
                                </button>
                                <button
                                    type="button"
                                    className={`w_120_d2 btn btn_primary w_fit_content`}
                                    onClick={handleSave}
                                    style={{
                                        pointerEvents: buttonProcessing ? "none" : "auto",
                                        opacity: buttonProcessing ? "0.7" : "1",
                                    }}
                                >
                                    {buttonProcessing ? <span><img src={loaderIcon} alt="" /> </span> : <span>Save Changes</span>}
                                </button>
                            </div>
                        </div>

                    </>
                }

                {!formVisible &&
                    <div className="d-flex justify-content-center align-items-center mt-5 pt-5">
                        <img src={FullLoader} className="mt-5" width={50} height={50} alt="" />
                    </div>
                }


            </div>


            {/* Block Category Modal */}
            <Modal
                className="custom_modal custom_modal_form_d2"
                open={blockCatModalOpen}
                onCancel={() => setBlockCatModalOpen(false)}
                width={770}
                maskClosable={false}
                title={
                    <div className="custom_modal--header">
                        <h3 className="heading">
                            <img src={creativetemicon} alt="USP" />
                            Select Sensitive Category to block
                        </h3>
                    </div>
                }
                closable={true}
                footer={[
                    <div key="footer" className="custom_modal--footer d_flex justify_content_end">
                        <div className="btn_group justify-content-end gap_8">
                            <button
                                className="w_120_d2 btn btn_white_outline w_fit_content"
                                onClick={() => setBlockCatModalOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="w_120_d2 btn btn_primary w_fit_content"
                                onClick={applyBlockCat}
                            >
                                Apply
                            </button>
                        </div>
                    </div>,
                ]}
            >
                <div className="modal_custom_body">
                    <BlockCategoryTable
                        selected={blockCatModalValue}
                        setSelected={setBlockCatModalValue}
                        normalizeKey={blockCatNormalizeKey}
                    />
                </div>
            </Modal>
        </>
    );
};

export default BulkZoneEdit;
